// src/renderers/ReactQuoteRenderer/ReactQuoteRenderer.tsx
import partition from "lodash/partition";
import { Box as Box8, Flex as Flex14 } from "@chakra-ui/react";
import { match as match9 } from "ts-pattern";
import {
  GreyGrey30 as GreyGrey305,
  Lato14Bold as Lato14Bold3,
  Lato14ExtendedRegular as Lato14ExtendedRegular3,
  Lato16Bold,
  Lato20Bold as Lato20Bold2
} from "@sequencehq/design-tokens";

// src/renderers/ReactQuoteRenderer/blocks/ImageBlock/ImageBlock.tsx
import { Box, Image as Image2 } from "@chakra-ui/react";
import { useGetSignedAssetUrl } from "@sequencehq/utils";
import { jsx } from "react/jsx-runtime";
var ImageBlock = ({
  config: { src, alt, width },
  assetLoader
}) => {
  const signedSrc = useGetSignedAssetUrl({ url: src, assetLoader });
  if (!signedSrc) {
    return null;
  }
  return /* @__PURE__ */ jsx(Box, { width: "100%", children: /* @__PURE__ */ jsx(Image2, { src: signedSrc, alt, width }) });
};

// src/renderers/ReactQuoteRenderer/blocks/BulletListBlock/BulletListBlock.tsx
import { ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { GreyGrey80, Lato14ExtendedRegular } from "@sequencehq/design-tokens";
import { jsx as jsx2 } from "react/jsx-runtime";
var BulletListBlock = ({ config: { items } }) => /* @__PURE__ */ jsx2(UnorderedList, { children: items.map((item, index) => /* @__PURE__ */ jsx2(
  ListItem,
  {
    marginBottom: index !== items.length - 1 ? "3px" : void 0,
    children: /* @__PURE__ */ jsx2(Text, { ...Lato14ExtendedRegular, color: GreyGrey80, children: item })
  },
  index
)) });

// src/renderers/ReactQuoteRenderer/blocks/NumberListBlock/NumberListBlock.tsx
import { ListItem as ListItem2, Text as Text2, OrderedList } from "@chakra-ui/react";
import { GreyGrey80 as GreyGrey802, Lato14ExtendedRegular as Lato14ExtendedRegular2 } from "@sequencehq/design-tokens";
import { jsx as jsx3 } from "react/jsx-runtime";
var NumberListBlock = ({ config: { items } }) => /* @__PURE__ */ jsx3(OrderedList, { children: items.map((item, index) => /* @__PURE__ */ jsx3(
  ListItem2,
  {
    ...Lato14ExtendedRegular2,
    marginBottom: index !== items.length - 1 ? "7px" : void 0,
    children: /* @__PURE__ */ jsx3(Text2, { ...Lato14ExtendedRegular2, color: GreyGrey802, children: item })
  },
  item
)) });

// src/renderers/ReactQuoteRenderer/blocks/TextBlock/TextBlock.tsx
import { Text as Text4 } from "@chakra-ui/react";

// src/renderers/ReactQuoteRenderer/formatting/formatting.tsx
import { Link, Text as Text3 } from "@chakra-ui/react";
import { Fragment } from "react";

// src/renderers/common/quoteRenderer.helpers.ts
import { formatDuration, format, addDurations } from "@sequencehq/utils/dates";
import { match } from "ts-pattern";

// src/renderers/common/quoteRenderer.guards.ts
var isTextBlockConfig = (obj) => "text" in obj;
var isImageBlockConfig = (obj) => "src" in obj && "alt" in obj && "width" in obj;
var isListBlockConfig = (obj) => "items" in obj;
var isListBlockConfigV2 = (obj) => "items" in obj;
var isListBlockConfigV3 = (obj) => "items" in obj;
var isPhaseBlockConfig = (obj) => "phases" in obj;
var isCoverImageBlockConfig = (obj) => "asset" in obj;
var isCoverImageBlockConfigV2 = (obj) => "assetPath" in obj;
var isQuoteBlock = (obj) => "blockType" in obj && "version" in obj && "config" in obj;

// src/renderers/common/quoteRenderer.helpers.ts
import { GreyGrey80 as GreyGrey803 } from "@sequencehq/design-tokens";

// src/renderers/common/quoteRenderer.constants.ts
var PHASE_TABLE_DATE_RANGE_FORMAT = "d MMM yyyy";
var OPEN_ENDED_LABEL = "Open ended";

// src/renderers/common/quoteRenderer.helpers.ts
var presentationBlocksToQuoteBlocks = (presentationBlocks) => {
  return presentationBlocks.map((block) => {
    if (!isQuoteBlock(block)) {
      return;
    }
    return match(block).with(
      { blockType: "bulletList" },
      { blockType: "numberList" },
      (thisBlock) => {
        if (thisBlock.version === 3 && isListBlockConfigV3(thisBlock.config)) {
          return block;
        }
        if (thisBlock.version === 2 && isListBlockConfigV2(thisBlock.config)) {
          return block;
        }
        if (thisBlock.version === 1 && isListBlockConfig(thisBlock.config)) {
          return block;
        }
        return;
      }
    ).with(
      { blockType: "heading1" },
      { blockType: "heading2" },
      { blockType: "heading3" },
      { blockType: "paragraph" },
      (thisBlock) => {
        if (!isTextBlockConfig(thisBlock.config)) {
          return;
        }
        return block;
      }
    ).with({ blockType: "image" }, (thisBlock) => {
      if (!isImageBlockConfig(thisBlock.config)) {
        return;
      }
      return block;
    }).with({ blockType: "phases" }, (thisBlock) => {
      if (!isPhaseBlockConfig(thisBlock.config)) {
        return;
      }
      return block;
    }).with({ blockType: "coverImage" }, (thisBlock) => {
      if (thisBlock.version === 2 && isCoverImageBlockConfigV2(thisBlock.config)) {
        return block;
      }
      if (thisBlock.version === 1 && isCoverImageBlockConfig(thisBlock.config)) {
        return block;
      }
      return;
    }).exhaustive();
  }).filter(Boolean);
};
var renderContractLength = (phases) => {
  if (phases.length === 0) {
    return void 0;
  }
  if (!phases.at(-1)?.duration) {
    return OPEN_ENDED_LABEL;
  }
  const durations = phases.map((phase) => phase.duration).filter(Boolean);
  const durationsTotal = durations.reduce(
    (acc, curr) => addDurations(acc, curr),
    {}
  );
  return formatDuration(durationsTotal, {
    format: ["years", "months", "days"]
  });
};
var renderPhaseDates = (dates) => {
  const dateStartResolved = (() => {
    {
      if (!dates.absolute.start) {
        return void 0;
      }
      return format(
        new Date(dates.absolute.start),
        PHASE_TABLE_DATE_RANGE_FORMAT
      );
    }
  })();
  const dateEndResolved = (() => {
    if (!dates.absolute.end) {
      return OPEN_ENDED_LABEL;
    }
    return format(new Date(dates.absolute.end), PHASE_TABLE_DATE_RANGE_FORMAT);
  })();
  const durationResolved = (() => {
    if (!dates.duration) {
      return void 0;
    }
    return formatDuration(
      {
        ...dates.duration,
        days: dates.duration.days === -1 ? 0 : dates.duration.days
      },
      { format: ["years", "months", "days"] }
    );
  })();
  const showDateRange = Boolean(dateStartResolved) && Boolean(dateEndResolved);
  return {
    isOpenEnded: !dates.absolute.end,
    dateStartResolved,
    dateEndResolved,
    durationResolved,
    showDateRange
  };
};
var mapTextColor = (textColor) => {
  return match(textColor).with("gray", () => "#9B9A97").with("brown", () => "#64473A").with("red", () => "#E03D3E").with("orange", () => "#D9730F").with("yellow", () => "#DFAB01").with("green", () => "#4E6461").with("blue", () => "#0D6E99").with("purple", () => "#6940A5").with("pink", () => "#AD1A72").otherwise(() => GreyGrey803);
};
var mapBackgroundColor = (backgroundColor) => {
  return match(backgroundColor).with("gray", () => "#EBECED").with("brown", () => "#E9E5E3").with("red", () => "#FBE4E4").with("orange", () => "#F6E9DB").with("yellow", () => "#FBF3DD").with("green", () => "#DEEDEA").with("blue", () => "#DEEBF1").with("purple", () => "#EAE4F1").with("pink", () => "#F4DFEB").otherwise(() => "transparent");
};
var filterDefaultVisibleColumns = (rowsWithUuid, defaultVisibleColumns) => {
  const showDiscount = Boolean(rowsWithUuid.find((row) => row.discount));
  if (showDiscount) {
    return defaultVisibleColumns;
  }
  return defaultVisibleColumns.filter((c) => c !== "discount");
};

// src/renderers/ReactQuoteRenderer/formatting/formatting.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var renderText = ({
  text,
  font
}) => {
  const textArray = Array.isArray(text) ? text : [text];
  return textArray.filter((textItem) => Boolean(textItem)).map((textItem) => {
    if (typeof textItem === "string") {
      return textItem;
    }
    return renderEnrichedText(font)(textItem);
  });
};
var mapStyles = (styles3, font) => {
  return {
    fontWeight: styles3.bold || font.fontWeight > 400 ? "bold" : "normal",
    fontStyle: styles3.italic ? "italic" : "normal",
    textDecoration: styles3.underline ? "underline" : "none",
    textDecorationLine: styles3.strike ? "line-through" : "none",
    color: styles3.textColor ? mapTextColor(styles3.textColor) : "inherit",
    backgroundColor: styles3.backgroundColor ? mapBackgroundColor(styles3.backgroundColor) : "transparent"
  };
};
var renderEnrichedText = (font) => (textItem) => {
  if (textItem.href) {
    return /* @__PURE__ */ jsx4(
      Link,
      {
        ...font,
        target: "_blank",
        href: textItem.href,
        ...mapStyles(textItem.styles, font),
        color: "#716fff",
        children: textItem.text
      }
    );
  }
  const lines = textItem.text.split("\n");
  return lines.map((line, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx4(Text3, { as: "span", ...font, ...mapStyles(textItem.styles, font), children: line }),
    index !== lines.length - 1 && /* @__PURE__ */ jsx4("br", {})
  ] }, index));
};

// src/renderers/ReactQuoteRenderer/blocks/TextBlock/TextBlock.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var TextBlock = ({ config, font }) => {
  const textAlignment = config.textAlignment ?? "left";
  const textColor = mapTextColor(config.textColor);
  const backgroundColor = mapBackgroundColor(config.backgroundColor);
  return /* @__PURE__ */ jsx5(
    Text4,
    {
      ...font,
      color: textColor,
      textAlign: textAlignment,
      minWidth: "1px",
      minHeight: font.fontSize,
      backgroundColor,
      children: renderText({ text: config.text, font })
    }
  );
};

// src/renderers/ReactQuoteRenderer/layout/Header.tsx
import { useMemo } from "react";
import { Flex as Flex4, Image as Image3, Text as Text7 } from "@chakra-ui/react";
import BuildingIcon from "@heroicons/react/16/solid/BuildingOfficeIcon";
import BriefcaseIcon from "@heroicons/react/16/solid/BriefcaseIcon";
import CalendarIcon from "@heroicons/react/16/solid/CalendarIcon";
import CalendarDaysIcon from "@heroicons/react/16/solid/CalendarDaysIcon";
import NoSymbolIcon from "@heroicons/react/16/solid/NoSymbolIcon";
import ArrowLongRightIcon from "@heroicons/react/16/solid/ArrowLongRightIcon";
import { UserCircleIcon as UserCircleIcon2 } from "@heroicons/react/16/solid";
import { GreyGrey100, GreyGrey60 as GreyGrey602, Lato24Bold } from "@sequencehq/design-tokens";
import { Pill, PillRow, PopoverWrapper } from "@sequencehq/core-components";

// src/renderers/common/widgets/PopoverCustomerCard/PopoverCustomerCard.tsx
import { Box as Box2, Button, Text as Text5, Flex } from "@chakra-ui/react";
import {
  GreyGrey30,
  GreyGrey80 as GreyGrey804,
  Lato13Bold,
  Lato13Regular
} from "@sequencehq/design-tokens";
import { useNavigate } from "react-router-dom";
import { jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var PopoverCustomerCard = ({
  legalName,
  email,
  address,
  linkEditCustomer,
  linkGoToCustomer
}) => {
  const navigate = useNavigate();
  const hasLinks = Boolean(linkEditCustomer) || Boolean(linkGoToCustomer);
  return /* @__PURE__ */ jsxs2(Box2, { width: "300px", children: [
    /* @__PURE__ */ jsxs2(Box2, { padding: "12px", children: [
      /* @__PURE__ */ jsx6(Text5, { ...Lato13Bold, lineHeight: "18px", color: GreyGrey804, children: legalName }),
      email && /* @__PURE__ */ jsx6(Text5, { ...Lato13Regular, lineHeight: "18px", color: GreyGrey804, children: email }),
      address.map((line, index) => /* @__PURE__ */ jsx6(
        Text5,
        {
          ...Lato13Regular,
          lineHeight: "18px",
          color: GreyGrey804,
          children: line
        },
        index
      ))
    ] }),
    hasLinks && /* @__PURE__ */ jsxs2(
      Flex,
      {
        paddingY: "8px",
        paddingX: "12px",
        gap: "8px",
        borderTop: `${GreyGrey30} 1px solid`,
        children: [
          linkEditCustomer && /* @__PURE__ */ jsx6(
            Button,
            {
              onClick: () => {
                navigate(linkEditCustomer);
              },
              variant: "component-library-secondary",
              flex: "1",
              children: "Edit details"
            }
          ),
          linkGoToCustomer && /* @__PURE__ */ jsx6(
            Button,
            {
              onClick: () => {
                navigate(linkGoToCustomer);
              },
              variant: "component-library-secondary",
              flex: "1",
              children: "Go to customer"
            }
          )
        ]
      }
    )
  ] });
};

// src/renderers/common/widgets/ContactRow/ContactRow.tsx
import { Text as Text6, Flex as Flex2, Center, Box as Box3 } from "@chakra-ui/react";
import { BuildingOfficeIcon, UserCircleIcon } from "@heroicons/react/16/solid";
import { Badge } from "@sequencehq/core-components";
import {
  GreyGrey80 as GreyGrey805,
  Lato13Regular as Lato13Regular2,
  Lato13Bold as Lato13Bold2,
  GreyGrey60,
  GreyGrey70
} from "@sequencehq/design-tokens";
import { Fragment as Fragment2, jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var ContactRow = ({
  name,
  email,
  signedAt,
  style,
  isCounterSigner = false,
  showSignedStatusBadge = false
}) => /* @__PURE__ */ jsxs3(
  Flex2,
  {
    borderBottom: style?.borderBottom,
    paddingX: style?.paddingX ?? "12px",
    paddingY: style?.paddingY ?? "8px",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    children: [
      /* @__PURE__ */ jsxs3(Flex2, { gap: "4px", flex: "1", overflow: "hidden", children: [
        /* @__PURE__ */ jsx7(
          Center,
          {
            boxSize: "16px",
            flexShrink: "0",
            marginRight: style?.iconMarginRight,
            children: /* @__PURE__ */ jsx7(UserCircleIcon, { width: "16px", height: "16px", color: GreyGrey70 })
          }
        ),
        /* @__PURE__ */ jsx7(
          Text6,
          {
            ...style?.nameFontWeight === "normal" ? Lato13Regular2 : Lato13Bold2,
            color: GreyGrey805,
            whiteSpace: "nowrap",
            children: name
          }
        ),
        /* @__PURE__ */ jsx7(
          Text6,
          {
            ...Lato13Regular2,
            color: GreyGrey60,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            children: email
          }
        ),
        /* @__PURE__ */ jsx7(Box3, { flex: 1 }),
        isCounterSigner && /* @__PURE__ */ jsx7(BuildingOfficeIcon, { width: 16 })
      ] }),
      showSignedStatusBadge && /* @__PURE__ */ jsx7(Fragment2, { children: signedAt ? /* @__PURE__ */ jsx7(Badge, { sentiment: "success", children: "Signed" }) : /* @__PURE__ */ jsx7(Badge, { sentiment: "highlight", children: "Pending" }) })
    ]
  }
);

// src/renderers/common/widgets/PopoverContacts/PopoverContacts.tsx
import { Flex as Flex3 } from "@chakra-ui/react";
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var PopoverContacts = ({
  contacts,
  counterSigners = [],
  width = "352px",
  showSignedStatusBadge = false
}) => {
  const defaultContactRowStyles = {
    nameFontWeight: "normal",
    paddingX: "8px",
    paddingY: "6px",
    iconMarginRight: "4px"
  };
  return /* @__PURE__ */ jsxs4(Flex3, { flexDirection: "column", padding: "4px", width, children: [
    contacts.map((contact) => /* @__PURE__ */ jsx8(
      ContactRow,
      {
        ...contact,
        style: defaultContactRowStyles,
        showSignedStatusBadge
      },
      contact.email
    )),
    counterSigners.map((contact) => /* @__PURE__ */ jsx8(
      ContactRow,
      {
        ...contact,
        isCounterSigner: true,
        style: defaultContactRowStyles,
        showSignedStatusBadge
      },
      contact.email
    ))
  ] });
};

// src/renderers/ReactQuoteRenderer/layout/Header.tsx
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var Header = ({ hook }) => {
  const titleStyle = useMemo(() => {
    return hook.title ? {
      ...Lato24Bold,
      color: GreyGrey100
    } : {
      ...Lato24Bold,
      color: GreyGrey602
    };
  }, [hook.title]);
  return /* @__PURE__ */ jsxs5(Flex4, { gap: "24px", direction: "column", children: [
    /* @__PURE__ */ jsxs5(Flex4, { justifyContent: "space-between", alignItems: "center", children: [
      /* @__PURE__ */ jsx9(Text7, { ...titleStyle, children: hook.title || "Untitled" }),
      hook.merchant.logoUrl && /* @__PURE__ */ jsx9(
        Image3,
        {
          src: hook.merchant.logoUrl,
          alt: `Logo for ${hook.merchant.legalName}`,
          height: "20px"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs5(PillRow, { children: [
      hook.prospect && /* @__PURE__ */ jsx9(
        PopoverWrapper,
        {
          popover: /* @__PURE__ */ jsx9(
            PopoverCustomerCard,
            {
              legalName: hook.prospect.legalName,
              address: hook.prospect.address
            }
          ),
          children: /* @__PURE__ */ jsx9(
            Pill,
            {
              icon: /* @__PURE__ */ jsx9(BuildingIcon, { width: "16px", height: "16px" }),
              "data-testid": "reactQuoteRenderer.header.prospectPill",
              children: hook.prospect.legalName
            }
          )
        }
      ),
      hook.contactsPill.isVisible && /* @__PURE__ */ jsx9(
        PopoverWrapper,
        {
          popover: /* @__PURE__ */ jsx9(PopoverContacts, { contacts: hook.contactsPill.contacts }),
          children: /* @__PURE__ */ jsx9(
            Pill,
            {
              icon: /* @__PURE__ */ jsx9(UserCircleIcon2, { width: "16px", height: "16px" }),
              more: hook.contactsPill.more,
              "data-testid": "reactQuoteRenderer.header.contactsPill",
              children: hook.contactsPill.leadContact
            }
          )
        }
      ),
      /* @__PURE__ */ jsx9(Pill, { icon: /* @__PURE__ */ jsx9(BriefcaseIcon, { width: "16px", height: "16px" }), children: hook.dealType }),
      hook.contractLength && /* @__PURE__ */ jsx9(Pill, { icon: /* @__PURE__ */ jsx9(CalendarIcon, { width: "16px", height: "16px" }), children: hook.contractLength }),
      hook.dateRangeStart && /* @__PURE__ */ jsx9(Pill, { icon: /* @__PURE__ */ jsx9(CalendarDaysIcon, { width: "16px", height: "16px" }), children: /* @__PURE__ */ jsxs5(Flex4, { gap: "4px", children: [
        /* @__PURE__ */ jsx9(
          Text7,
          {
            as: "span",
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
            children: hook.dateRangeStart
          }
        ),
        hook.dateRangeEnd && /* @__PURE__ */ jsxs5(Fragment3, { children: [
          /* @__PURE__ */ jsx9(ArrowLongRightIcon, { width: "16px", height: "16px" }),
          /* @__PURE__ */ jsx9(
            Text7,
            {
              as: "span",
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              children: hook.dateRangeEnd
            }
          )
        ] })
      ] }) }),
      hook.validUntil && /* @__PURE__ */ jsx9(Pill, { icon: /* @__PURE__ */ jsx9(NoSymbolIcon, { width: 16, height: 16 }), children: hook.validUntil })
    ] })
  ] });
};

// src/renderers/ReactQuoteRenderer/hooks/useReactQuoteRenderer.tsx
import { useMemo as useMemo2 } from "react";
import { v4 as uuidv4 } from "uuid";
import { match as match2 } from "ts-pattern";
import { add, format as format2, addDurations as addDurations2 } from "@sequencehq/utils/dates";

// src/adapters/apiPresentationToQuotePresentation.ts
var apiPresentationToQuotePresentation = (presentation) => {
  if (Array.isArray(presentation)) {
    return {
      version: 2,
      blocks: presentationBlocksToQuoteBlocks(presentation),
      settings: {
        showTotals: false,
        groupPricesByPhase: false
      }
    };
  }
  return {
    version: 2,
    blocks: presentationBlocksToQuoteBlocks(presentation.blocks),
    settings: {
      showTotals: false,
      groupPricesByPhase: presentation.settings.groupPricesByPhase
    }
  };
};

// src/renderers/ReactQuoteRenderer/hooks/useReactQuoteRenderer.tsx
var useReactQuoteRenderer = ({ data }) => {
  const blocks = useMemo2(() => {
    const presentation = apiPresentationToQuotePresentation(data.presentation);
    return presentation.blocks.map((block) => ({
      ...block,
      uuid: uuidv4()
    }));
  }, [data.presentation]);
  const settings = useMemo2(() => {
    const presentation = apiPresentationToQuotePresentation(data.presentation);
    return presentation.settings;
  }, [data.presentation]);
  const contractLength = useMemo2(() => {
    return renderContractLength(data.phases);
  }, [data.phases]);
  const startDate = useMemo2(() => {
    if (!data.preliminaryBillingStartDate) {
      return void 0;
    }
    return format2(new Date(data.preliminaryBillingStartDate), "d LLLL yyyy");
  }, [data.preliminaryBillingStartDate]);
  const validUntil = useMemo2(() => {
    if (!data.expiresAt) {
      return void 0;
    }
    return format2(new Date(data.expiresAt), "d LLLL yyyy");
  }, [data.expiresAt]);
  const dealType = useMemo2(() => {
    return match2(data.dealType).with("NEW_BUSINESS", () => "New Business").with("RENEWAL", () => "Renewal").exhaustive();
  }, [data.dealType]);
  const dateRangeStart = useMemo2(() => {
    if (!data.preliminaryBillingStartDate) {
      return void 0;
    }
    return format2(new Date(data.preliminaryBillingStartDate), "d LLLL yyyy");
  }, [data.preliminaryBillingStartDate]);
  const dateRangeEnd = useMemo2(() => {
    if (!data.preliminaryBillingStartDate) {
      return void 0;
    }
    const startDateUtc = new Date(data.preliminaryBillingStartDate);
    const allPhaseDurations = data.phases.map((phase) => phase.duration).filter(Boolean);
    if (allPhaseDurations.length === 0) {
      return void 0;
    }
    const totalPhaseDurations = allPhaseDurations.reduce((acc, curr) => {
      return addDurations2(acc, curr);
    }, {});
    const endDate = add(startDateUtc, { ...totalPhaseDurations, days: -1 });
    return format2(endDate, "d LLLL yyyy");
  }, [data.phases, data.preliminaryBillingStartDate]);
  const contactsPill = useMemo2(() => {
    const contacts = data.contacts.map((contact) => ({
      name: contact.name ?? contact.email,
      email: contact.name ? contact.email : "",
      signedAt: contact.signedAt
    }));
    return {
      contacts,
      leadContact: contacts[0]?.name ?? "",
      more: contacts.length > 1 ? contacts.length - 1 : void 0,
      isVisible: contacts.length > 0
    };
  }, [data.contacts]);
  return {
    blocks,
    settings,
    title: data.title,
    merchant: {
      logoUrl: data.merchantLogoUrl,
      legalName: data.merchantName,
      address: data.merchantAddress,
      email: data.merchantEmailAddress
    },
    prospect: data.prospectName && data.prospectAddress ? {
      legalName: data.prospectName,
      address: data.prospectAddress
    } : void 0,
    contractLength,
    startDate,
    validUntil,
    dealType,
    dateRangeStart,
    dateRangeEnd,
    contactsPill
  };
};

// src/renderers/common/usePhaseBlock/usePhaseBlock.ts
import { useMemo as useMemo3 } from "react";
import { v4 as uuidv42 } from "uuid";

// src/renderers/common/usePhaseBlock/renderMinimums/renderMinimums.ts
import { toBillingFrequencyName, toMoney } from "@sequencehq/core-models";
import { match as match3 } from "ts-pattern";
var renderMinimums = ({
  config
}) => {
  const phasesWithMinimums = config.phases.filter(
    (phase) => phase.minimums.length > 0
  );
  if (phasesWithMinimums.length === 0) {
    return [];
  }
  const showDateInDescription = allPhasesHaveSameMinimums({ allPhases: config.phases }) === false;
  if (allPhasesHaveSameMinimums({ allPhases: config.phases })) {
    return [
      {
        title: {
          type: "TEXT",
          text: "Minimum commitment"
        },
        rows: renderMinimumRowsForPhase({
          phase: phasesWithMinimums[0],
          showDateInDescription
        })
      }
    ];
  }
  return [
    {
      title: {
        type: "TEXT",
        text: "Minimum commitment"
      },
      rows: phasesWithMinimums.flatMap(
        (phase) => renderMinimumRowsForPhase({
          phase,
          showDateInDescription
        })
      )
    }
  ];
};
var allPhasesHaveSameMinimums = ({
  allPhases
}) => {
  const eachPhaseHasMinimum = Array.from([
    ...new Set(allPhases.map((phase) => Boolean(phase.minimums.length > 0)))
  ]).length === 1;
  if (!eachPhaseHasMinimum) {
    return false;
  }
  const eachPhaseHasSameValue = Array.from([...new Set(allPhases.map((phase) => phase.minimums[0].value))]).length === 1;
  if (!eachPhaseHasSameValue) {
    return false;
  }
  const allPhaseMinimumTargets = Array.from([
    ...new Set(allPhases.map((phase) => phase.minimums[0].scope.target))
  ]);
  const eachPhaseHasSameTarget = allPhaseMinimumTargets.length === 1;
  if (eachPhaseHasSameTarget && allPhaseMinimumTargets[0] === "allUsage") {
    return true;
  }
  const productIdsInPhases = allPhases.map((phase) => {
    const thisPhaseProductIds = (() => {
      return match3(phase.minimums[0].scope.target).with("allUsage", () => {
        const allProductIdsWithUsage = phase.prices.filter((price) => "usageMetricId" in price.structure).map((price) => price.productId).sort();
        return allProductIdsWithUsage;
      }).with("specific", () => {
        return phase.minimums[0].scope.priceIds.map(
          (priceId) => phase.prices.find((price) => price.id === priceId)?.productId
        ).filter(Boolean).sort();
      }).exhaustive();
    })();
    return thisPhaseProductIds.join(",");
  });
  const selectedProductsAreSame = Array.from([...new Set(productIdsInPhases)]).length === 1;
  return selectedProductsAreSame;
};
var renderMinimumRowsForPhase = ({
  phase,
  showDateInDescription
}) => {
  const minimum = phase.minimums[0];
  const assumedCurrency = assumeMinimumsCurrencyFromPrices(phase.prices);
  const assumedFrequency = assumeBillingFrequencyFromPrices(
    phase.prices,
    minimum
  );
  const productNamesIncludedInMinimum = getProductNamesIncludedInMinimum(phase);
  const productNameRows = productNamesIncludedInMinimum.map(
    (name) => ({
      description: {
        type: "TEXT",
        text: name
      },
      indentation: 1
    })
  );
  return [
    {
      description: showDateInDescription ? {
        type: "DATE_RANGE",
        dates: phase.dates
      } : {
        type: "TEXT",
        text: "Minimum commitment",
        style: { fontWeight: "bold" }
      },
      quantity: {
        type: "TEXT",
        text: "1"
      },
      rate: {
        type: "TEXT",
        text: assumedCurrency ? [
          toMoney({
            currency: assumedCurrency,
            value: minimum.value
          }),
          assumedFrequency
        ].join(" / ") : ""
      }
    },
    ...productNameRows
  ];
};
var assumeMinimumsCurrencyFromPrices = (prices) => {
  if (prices.length === 0) {
    return void 0;
  }
  return prices[0].currency;
};
var assumeBillingFrequencyFromPrices = (prices, minimum) => {
  return match3(minimum.scope.target).with("allUsage", () => {
    const pricesWithUsageMetricId = prices.filter(
      (price) => "usageMetricId" in price.structure
    );
    return pricesWithUsageMetricId[0]?.billingFrequency ? toBillingFrequencyName(pricesWithUsageMetricId[0].billingFrequency) : "";
  }).with("specific", () => {
    const specificPrices = prices.filter(
      (price) => minimum.scope.priceIds.includes(price.id)
    );
    return specificPrices[0]?.billingFrequency ? toBillingFrequencyName(specificPrices[0].billingFrequency) : "";
  }).exhaustive();
};
var getProductNamesIncludedInMinimum = (phase) => {
  if (!phase.minimums[0]) {
    return [];
  }
  return match3(phase.minimums[0].scope.target).with("allUsage", () => {
    const pricesWithUsageMetricId = phase.prices.filter(
      (price) => "usageMetricId" in price.structure
    );
    return pricesWithUsageMetricId.map((price) => price.name);
  }).with("specific", () => {
    const specificPriceNames = phase.minimums[0].scope.priceIds.map((priceId) => phase.prices.find((price) => price.id === priceId)?.name).filter(Boolean);
    return specificPriceNames;
  }).exhaustive();
};

// src/renderers/common/usePhaseBlock/renderProducts/renderProducts.ts
import { match as match5 } from "ts-pattern";

// src/renderers/common/usePhaseBlock/renderProducts/renderProducts.helpers.ts
import { dequal as dequal2 } from "dequal";
import {
  toBillingFrequencyName as toBillingFrequencyName2,
  toMoney as toMoney2
} from "@sequencehq/core-models";

// src/renderers/common/usePhaseBlock/calculateSeatConsumption/calculateSeatConsumption.ts
var calculateSeatConsumption = ({
  price,
  includedSeats
}) => {
  if (price.structure.pricingType !== "SEAT_BASED" || !("tiers" in price.structure) || price.structure.tiers.length === 0) {
    return [];
  }
  const tiers = price.structure.tiers;
  return tiers.map((tier, tierIndex) => {
    const minInclusive = tierIndex === 0 ? 1 : Number(tiers[tierIndex - 1].upperBound) + 1;
    const maxInclusive = tier.upperBound ? Number(tier.upperBound) : Infinity;
    const seatsConsumed = Array.from({ length: includedSeats }).reduce(
      (acc, _, seatsConsumedIndex) => {
        const seatNumber = seatsConsumedIndex + 1;
        if (seatNumber >= minInclusive && seatNumber <= maxInclusive) {
          return acc + 1;
        }
        return acc;
      },
      0
    );
    const pricePerSeat = Number(tier.price);
    return {
      minInclusive,
      maxInclusive,
      seatsConsumed,
      pricePerSeat,
      fee: tier.fee ? Number(tier.fee) : void 0
    };
  });
};

// src/renderers/common/usePhaseBlock/isDiscountSameAcrossPhases/isDiscountSameAcrossPhases.ts
import { dequal } from "dequal";
var isDiscountSameAcrossPhases = ({
  productId,
  phasesWithProductId
}) => {
  if (phasesWithProductId.length <= 1) {
    return true;
  }
  const areDiscountsUsed = phasesWithProductId.some(
    (phase) => phase.discounts.length > 0
  );
  if (!areDiscountsUsed) {
    return true;
  }
  const parsedDiscounts = phasesWithProductId.map((phase) => {
    const { discounts } = phase;
    if (discounts.length === 0) {
      return { hasDiscount: false };
    }
    if (discounts[0] && discounts[0].applyToAllPrices) {
      return {
        hasDiscount: true,
        amount: discounts[0].amount,
        discountCalculationType: discounts[0].discountCalculationType
      };
    }
    const priceId = phase.prices.find(
      (price) => price.productId === productId
    )?.id;
    if (!priceId) {
      throw new Error();
    }
    const discountWithPriceId = discounts.find(
      (discount) => discount.priceIds.includes(priceId)
    );
    if (!discountWithPriceId) {
      return { hasDiscount: false };
    }
    return {
      hasDiscount: true,
      amount: discountWithPriceId.amount,
      discountCalculationType: discountWithPriceId.discountCalculationType
    };
  });
  const firstDiscount = parsedDiscounts[0];
  return parsedDiscounts.map((discount) => dequal(firstDiscount, discount)).includes(false) === false;
};

// src/renderers/common/usePhaseBlock/renderProducts/renderProducts.constants.ts
var VOLUME_CAPTION = "The rate is based on the final tier reached.";
var GRADUATED_CAPTION = "Each tier applies progressively as quantity increases.";
var SEAT_BASED_GRADUATED_CAPTION = "Each tier applies progressively as quantity increases.";
var styles = {
  description: { fontWeight: "bold" },
  varies: { color: "GreyGrey60" },
  discountedRate: {
    textDecoration: "line-through",
    color: "GreyGrey60"
  }
};
var strings = {
  varies: "Varies",
  variesWithUsage: "Varies with usage",
  variesWithSeatUsage: "Varies with seat usage",
  usageBased: "Usage-based",
  seatBasedContractedMinimum: "contracted"
};

// src/renderers/common/usePhaseBlock/renderProducts/renderProducts.helpers.ts
import { match as match4 } from "ts-pattern";
var getRelevantDiscount = ({
  price,
  phasesWithProductId
}) => {
  const relevantPhase = phasesWithProductId.find(
    (phase) => phase.prices.some((p) => p.id === price.id)
  );
  if (!relevantPhase) {
    return void 0;
  }
  if (relevantPhase.discounts.length === 0) {
    return void 0;
  }
  if (relevantPhase.discounts[0] && relevantPhase.discounts[0].applyToAllPrices) {
    return relevantPhase.discounts[0];
  }
  return relevantPhase.discounts.find(
    (discount) => discount.priceIds.includes(price.id)
  );
};
var getDiscountedRate = ({
  amount,
  discount
}) => {
  if (discount.discountCalculationType === "NOMINAL") {
    return Math.max(0, amount - discount.amount);
  }
  if (discount.discountCalculationType === "PERCENTAGE") {
    return amount * ((100 - discount.amount) / 100);
  }
  return amount;
};
var getDiscountMessage = ({
  discount,
  currency
}) => {
  if (discount.discountCalculationType === "NOMINAL") {
    const amount = toMoney2({
      currency,
      value: String(discount.amount)
    });
    return `${amount} off`;
  }
  if (discount.discountCalculationType === "PERCENTAGE") {
    return `${discount.amount}% off`;
  }
  return;
};
var renderFixedOneTimePrice = ({
  price,
  phasesWithProductId,
  descriptionType
}) => {
  if (price.structure.pricingType !== "FIXED" && price.structure.pricingType !== "ONE_TIME") {
    return [];
  }
  const relevantDiscount = getRelevantDiscount({
    phasesWithProductId,
    price
  });
  const description = getDescription({
    descriptionType,
    price,
    phasesWithProductId
  });
  const quantity = {
    type: "TEXT",
    text: "1"
  };
  const discount = (() => {
    if (relevantDiscount) {
      return {
        type: "TEXT",
        text: getDiscountMessage({
          discount: relevantDiscount,
          currency: price.currency
        })
      };
    }
    return void 0;
  })();
  const discountedRate = (() => {
    if (relevantDiscount) {
      return {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          value: price.structure.price,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType,
          rateOnly: true
        }),
        style: styles.discountedRate
      };
    }
    return void 0;
  })();
  const rate = (() => {
    if (relevantDiscount) {
      return {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          value: `${getDiscountedRate({
            amount: Number(price.structure.price),
            discount: relevantDiscount
          })}`,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType
        })
      };
    }
    return {
      type: "TEXT",
      text: renderRate({
        currency: price.currency,
        value: price.structure.price,
        billingFrequency: price.billingFrequency,
        billingType: price.billingType
      })
    };
  })();
  return [
    {
      description,
      quantity,
      discount,
      discountedRate,
      rate
    }
  ];
};
var renderLinearPrice = ({
  price,
  phasesWithProductId,
  hasCollapsibleMinMaxRates,
  descriptionType
}) => {
  if (price.structure.pricingType !== "LINEAR") {
    return [];
  }
  const relevantDiscount = getRelevantDiscount({ phasesWithProductId, price });
  const rowMinPrice = price.structure.minPrice ? {
    description: {
      type: "TEXT",
      text: price.structure.isPricePercentage ? "Min. unit rate" : "Min. price"
    },
    rate: {
      type: "TEXT",
      text: renderRate({
        currency: price.currency,
        rateLabel: price.structure.isPricePercentage ? "per unit" : void 0,
        value: price.structure.minPrice,
        billingFrequency: price.billingFrequency,
        billingType: price.billingType
      })
    },
    indentation: 1
  } : void 0;
  const rowMaxPrice = price.structure.maxPrice ? {
    description: {
      type: "TEXT",
      text: price.structure.isPricePercentage ? "Max. unit rate" : "Max. price"
    },
    rate: {
      type: "TEXT",
      text: renderRate({
        currency: price.currency,
        rateLabel: price.structure.isPricePercentage ? "per unit" : void 0,
        value: price.structure.maxPrice,
        billingFrequency: price.billingFrequency,
        billingType: price.billingType
      })
    },
    indentation: 1
  } : void 0;
  const subrows = [
    ...rowMinPrice ? [rowMinPrice] : [],
    ...rowMaxPrice ? [rowMaxPrice] : []
  ];
  return [
    {
      description: getDescription({
        descriptionType,
        price,
        phasesWithProductId
      }),
      quantity: {
        type: "TEXT",
        text: strings.usageBased,
        style: styles.varies
      },
      rate: {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          rateLabel: price.structure.isPricePercentage ? "of volume" : "per unit",
          value: price.structure.pricePerUnit,
          isPricePercentage: price.structure.isPricePercentage,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType
        })
      },
      discount: relevantDiscount ? {
        type: "TEXT",
        text: getDiscountMessage({
          discount: relevantDiscount,
          currency: price.currency
        })
      } : void 0,
      ...hasCollapsibleMinMaxRates && subrows.length > 0 ? { subrows } : {}
    },
    ...!hasCollapsibleMinMaxRates ? subrows : []
  ];
};
var renderVolumeGraduatedPrice = ({
  price,
  phasesWithProductId,
  descriptionType,
  hasCollapsibleTiers
}) => {
  if (price.structure.pricingType !== "VOLUME" && price.structure.pricingType !== "GRADUATED") {
    return [];
  }
  const relevantDiscount = getRelevantDiscount({ phasesWithProductId, price });
  const subrows = [...renderVolumeGraduatedTiers({ price })];
  return [
    {
      description: getDescription({
        descriptionType,
        price,
        phasesWithProductId
      }),
      quantity: {
        type: "TEXT",
        text: strings.usageBased,
        style: styles.varies
      },
      rate: {
        type: "TEXT",
        text: strings.variesWithUsage,
        style: styles.varies
      },
      discount: relevantDiscount ? {
        type: "TEXT",
        text: getDiscountMessage({
          discount: relevantDiscount,
          currency: price.currency
        })
      } : void 0,
      ...hasCollapsibleTiers ? { subrows } : {}
    },
    ...!hasCollapsibleTiers ? subrows : []
  ];
};
var renderVolumeGraduatedTiers = ({
  price,
  unitText = "units"
}) => {
  if (!("tiers" in price.structure)) {
    return [];
  }
  return price.structure.tiers.flatMap((tier, index) => {
    if (!("tiers" in price.structure)) {
      return void 0;
    }
    const previousTier = price.structure.tiers[index - 1];
    const description = (() => {
      if (tier.isPricePercentage) {
        return `${previousTier && previousTier.upperBound ? toMoney2({
          currency: price.currency,
          value: (parseFloat(previousTier.upperBound) + 0.01).toString()
        }) : toMoney2({
          currency: price.currency,
          value: "0.00"
        })} ${tier.upperBound ? `to ${toMoney2({
          currency: price.currency,
          value: tier.upperBound
        })}` : "and above"}`;
      }
      return `${previousTier && previousTier.upperBound ? `${(Number(previousTier.upperBound) + 1).toLocaleString()}` : "1"} ${tier.upperBound ? `to ${Number(tier.upperBound).toLocaleString()} ${unitText}` : `${unitText} and above`}`;
    })();
    const flatFeeDescription = (() => {
      if (index === 0 && tier.isPricePercentage) {
        return `Flat fee for first ${toMoney2({
          currency: price.currency,
          value: tier.upperBound || "0.00"
        })}`;
      }
      if (index === 0 && !tier.isPricePercentage) {
        return `Flat fee for first ${tier.upperBound} ${unitText}`;
      }
      return `Flat fee for ${description}`;
    })();
    return [
      {
        description: {
          type: "TEXT",
          text: `From ${description}`
        },
        rate: {
          type: "TEXT",
          text: renderRate({
            currency: price.currency,
            rateLabel: tier.isPricePercentage ? "of volume" : "per unit",
            value: tier.price,
            isPricePercentage: tier.isPricePercentage,
            billingFrequency: price.billingFrequency,
            billingType: price.billingType
          })
        },
        indentation: 1
      },
      ...tier.fee && Number(tier.fee) !== 0 ? [
        {
          description: {
            type: "TEXT",
            text: flatFeeDescription
          },
          rate: {
            type: "TEXT",
            text: renderRate({
              currency: price.currency,
              value: tier.fee,
              billingFrequency: price.billingFrequency,
              billingType: price.billingType
            })
          },
          indentation: 2
        }
      ] : [],
      ...tier.minPrice ? [
        {
          description: {
            type: "TEXT",
            text: tier.isPricePercentage ? "Min. unit rate" : "Min. fee"
          },
          rate: {
            type: "TEXT",
            text: renderRate({
              currency: price.currency,
              rateLabel: tier.isPricePercentage ? "per unit" : void 0,
              value: tier.minPrice,
              billingFrequency: price.billingFrequency,
              billingType: price.billingType
            })
          },
          indentation: 2
        }
      ] : [],
      ...tier.maxPrice ? [
        {
          description: {
            type: "TEXT",
            text: tier.isPricePercentage ? "Max. unit rate" : "Max. fee"
          },
          rate: {
            type: "TEXT",
            text: renderRate({
              currency: price.currency,
              rateLabel: tier.isPricePercentage ? "per unit" : void 0,
              value: tier.maxPrice,
              billingFrequency: price.billingFrequency,
              billingType: price.billingType
            })
          },
          indentation: 2
        }
      ] : []
    ];
  }).filter(Boolean);
};
var renderPackagePrice = ({
  price,
  phasesWithProductId,
  descriptionType
}) => {
  if (price.structure.pricingType !== "PACKAGE") {
    return [];
  }
  const relevantDiscount = getRelevantDiscount({ phasesWithProductId, price });
  const description = getDescription({
    descriptionType,
    price,
    phasesWithProductId,
    additionalText: `(${price.structure.packageSize} units per package)`
  });
  const quantity = {
    type: "TEXT",
    text: strings.usageBased,
    style: styles.varies
  };
  const discount = (() => {
    if (relevantDiscount) {
      return {
        type: "TEXT",
        text: getDiscountMessage({
          discount: relevantDiscount,
          currency: price.currency
        })
      };
    }
    return void 0;
  })();
  const discountedRate = (() => {
    if (relevantDiscount) {
      return {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          value: price.structure.pricePerPackage,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType,
          rateOnly: true
        }),
        style: styles.discountedRate
      };
    }
    return void 0;
  })();
  const rate = (() => {
    if (relevantDiscount) {
      return {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          rateLabel: "per unit",
          value: `${getDiscountedRate({
            amount: Number(price.structure.pricePerPackage),
            discount: relevantDiscount
          })}`,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType
        })
      };
    }
    return {
      type: "TEXT",
      text: renderRate({
        currency: price.currency,
        rateLabel: "per unit",
        value: price.structure.pricePerPackage,
        billingFrequency: price.billingFrequency,
        billingType: price.billingType
      })
    };
  })();
  return [
    {
      description,
      quantity,
      discount,
      discountedRate,
      rate
    }
  ];
};
var renderSeatBasedPrice = ({
  price,
  phasesWithProductId,
  groupBy = "product",
  descriptionType,
  hasCollapsibleTiers
}) => {
  if (price.structure.pricingType !== "SEAT_BASED") {
    return [];
  }
  const relevantDiscount = getRelevantDiscount({ phasesWithProductId, price });
  const hasContractedMinimum = Boolean(price.structure.includedSeats);
  const isGraduated = "tiers" in price.structure && price.structure.tiers.length > 0;
  if (isGraduated) {
    const graduatedRows = [
      {
        description: getDescription({
          descriptionType,
          price,
          phasesWithProductId
        }),
        ...hasContractedMinimum ? {
          quantity: {
            type: "TEXT",
            text: `${String(
              price.structure.includedSeats
            )} (${strings.seatBasedContractedMinimum})`
          }
        } : {
          quantity: {
            type: "TEXT",
            text: strings.variesWithSeatUsage,
            style: styles.varies
          }
        },
        rate: {
          type: "TEXT",
          text: strings.varies,
          style: styles.varies
        },
        discount: relevantDiscount ? {
          type: "TEXT",
          text: getDiscountMessage({
            discount: relevantDiscount,
            currency: price.currency
          })
        } : void 0,
        ...hasCollapsibleTiers ? {
          subrows: [
            ...renderSeatBasedTiers({
              price,
              baseIndentation: groupBy === "phase" && hasContractedMinimum ? 1 : 0
            })
          ]
        } : {}
      },
      /**
       * Show the contracted minimum on a separate row when a quote is grouped by phases
       */
      ...groupBy === "phase" && hasContractedMinimum ? [
        {
          description: {
            type: "TEXT",
            text: `Contracted minimum: ${String(
              price.structure.includedSeats
            )} ${price.structure.includedSeats === 1 ? "seat" : "seats"}`
          },
          indentation: 1
        }
      ] : [],
      ...!hasCollapsibleTiers ? [
        ...renderSeatBasedTiers({
          price,
          baseIndentation: groupBy === "phase" && hasContractedMinimum ? 1 : 0
        })
      ] : []
    ];
    return graduatedRows;
  }
  const rows = [
    {
      description: getDescription({
        descriptionType,
        price,
        phasesWithProductId
      }),
      ...hasContractedMinimum ? {
        quantity: {
          type: "TEXT",
          text: `${String(
            price.structure.includedSeats
          )} (${strings.seatBasedContractedMinimum})`
        }
      } : {
        quantity: {
          type: "TEXT",
          text: strings.variesWithSeatUsage,
          style: styles.varies
        }
      },
      discount: relevantDiscount ? {
        type: "TEXT",
        text: getDiscountMessage({
          discount: relevantDiscount,
          currency: price.currency
        })
      } : void 0,
      discountedRate: relevantDiscount ? {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          value: price.structure.pricePerSeat,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType,
          rateOnly: true
        }),
        style: styles.discountedRate
      } : void 0,
      rate: {
        type: "TEXT",
        text: renderRate({
          currency: price.currency,
          rateLabel: "per seat",
          value: relevantDiscount ? `${getDiscountedRate({
            amount: Number(price.structure.pricePerSeat),
            discount: relevantDiscount
          })}` : price.structure.pricePerSeat,
          billingFrequency: price.billingFrequency,
          billingType: price.billingType
        })
      }
    },
    /**
     * Show the contracted minimum on a separate row when a quote is grouped by phases
     */
    ...groupBy === "phase" && hasContractedMinimum ? [
      {
        description: {
          type: "TEXT",
          text: `Contracted minimum: ${String(
            price.structure.includedSeats
          )} ${price.structure.includedSeats === 1 ? "seat" : "seats"}`
        },
        indentation: 1
      }
    ] : []
  ];
  return rows;
};
var renderSeatBasedTiers = ({
  price,
  baseIndentation = 0
}) => {
  if (!("tiers" in price.structure) || price.structure.tiers.length === 0 || price.structure.pricingType !== "SEAT_BASED") {
    return [];
  }
  const tiers = calculateSeatConsumption({
    price,
    includedSeats: price.structure.includedSeats
  });
  const tierIndentation = baseIndentation === 0 ? 1 : 2;
  const feeIndentation = baseIndentation === 0 ? 2 : 3;
  return tiers.flatMap((tier, index) => {
    const description = (() => {
      return `${tier.minInclusive.toLocaleString()} ${tier.maxInclusive !== Infinity ? `to ${tier.maxInclusive.toLocaleString()} seats` : `seats and above`}`;
    })();
    const flatFeeDescription = (() => {
      if (index === 0) {
        return `Flat fee for first ${tier.maxInclusive.toString()} seats`;
      }
      return `Flat fee for ${description}`;
    })();
    return [
      {
        description: {
          type: "TEXT",
          text: `From ${description}`
        },
        ...tier.seatsConsumed > 0 ? {
          quantity: {
            type: "TEXT",
            text: String(tier.seatsConsumed)
          }
        } : {},
        rate: {
          type: "TEXT",
          text: renderRate({
            currency: price.currency,
            rateLabel: "per seat",
            value: String(tier.pricePerSeat),
            billingFrequency: price.billingFrequency,
            billingType: price.billingType
          })
        },
        indentation: tierIndentation
      },
      ...tier.fee ? [
        {
          description: {
            type: "TEXT",
            text: flatFeeDescription
          },
          rate: {
            type: "TEXT",
            text: renderRate({
              currency: price.currency,
              value: String(tier.fee),
              billingFrequency: price.billingFrequency,
              billingType: price.billingType
            })
          },
          indentation: feeIndentation
        }
      ] : []
    ];
  });
};
var shouldMergePhasesIntoOneRow = ({
  prices,
  phasesWithProductId,
  allPhases,
  productId
}) => {
  const structures = prices.map((price) => price.structure);
  const firstStructure = structures[0];
  const isStructuresSame = structures.map((structure) => dequal2(firstStructure, structure)).includes(false) === false;
  const isDiscountsSame = isDiscountSameAcrossPhases({
    productId,
    phasesWithProductId
  });
  const isPriceInAllPhases = phasesWithProductId.length === allPhases.length;
  return isStructuresSame && isDiscountsSame && isPriceInAllPhases;
};
var renderRate = ({
  currency,
  value,
  billingFrequency,
  billingType,
  isPricePercentage = false,
  rateLabel,
  rateOnly = false
}) => {
  const renderedValue = isPricePercentage ? `${value}%` : toMoney2({ currency, value }, void 0, 10);
  return [
    rateLabel ? `${renderedValue} ${rateLabel}` : renderedValue,
    ...rateOnly ? [] : [
      billingFrequency ? toBillingFrequencyName2(billingFrequency) : void 0,
      ...billingType === "IN_ADVANCE" ? ["In advance"] : []
    ]
  ].filter(Boolean).join(" / ");
};
var getDescription = ({
  descriptionType,
  price,
  phasesWithProductId,
  additionalText
}) => {
  const phaseForPrice = phasesWithProductId.find(
    (phase) => phase.prices.flatMap((p) => p.id).includes(price.id)
  );
  if (!phaseForPrice) {
    throw new Error("unable to find phase with this price");
  }
  return match4(descriptionType).with("PHASE_LABEL", () => {
    if (phaseForPrice.name) {
      return {
        type: "TEXT",
        text: [phaseForPrice.name, additionalText].filter(Boolean).join(" ")
      };
    }
    return {
      type: "DATE_RANGE",
      dates: phaseForPrice.dates,
      ...additionalText ? { additionalText } : {}
    };
  }).with("PRICE_NAME", () => {
    return {
      type: "TEXT",
      text: [price.name, additionalText].filter(Boolean).join(" "),
      style: styles.description
    };
  }).exhaustive();
};

// src/renderers/common/usePhaseBlock/renderProducts/renderProducts.ts
var renderProducts = ({
  config,
  context = "react"
}) => {
  const { phases } = config;
  const allPrices = phases.flatMap(({ prices }) => prices);
  const allUniqueProductIds = Array.from([
    ...new Set(allPrices.map((price) => price.productId))
  ]);
  return allUniqueProductIds.flatMap((productId) => {
    const phasesWithProductId = phases.filter(
      (phase) => phase.prices.some((price) => price.productId === productId)
    );
    const pricesForProductIdWithPhase = phasesWithProductId.flatMap(
      (phase) => phase.prices.filter((price) => price.productId === productId).map((price) => ({ price, phase }))
    );
    if (pricesForProductIdWithPhase.length === 0) {
      return void 0;
    }
    const firstPrice = pricesForProductIdWithPhase[0].price;
    const allPricesUseSameStructure = !pricesForProductIdWithPhase.some(
      ({ price }) => price.structure.pricingType !== firstPrice.structure.pricingType
    );
    const title = {
      type: "TEXT",
      text: firstPrice.name
    };
    const caption = allPricesUseSameStructure ? getCaption(firstPrice) : void 0;
    const mergePhasesIntoOneRow = shouldMergePhasesIntoOneRow({
      prices: pricesForProductIdWithPhase.map((p) => p.price),
      phasesWithProductId,
      productId,
      allPhases: phases
    });
    if (mergePhasesIntoOneRow) {
      return {
        title,
        ...caption ? { caption } : {},
        rows: renderPriceRows({
          pricingType: firstPrice.structure.pricingType,
          price: firstPrice,
          phasesWithProductId,
          descriptionType: "PRICE_NAME",
          isCollapsible: false
        })
      };
    }
    const PRICING_TYPES_WITH_COLLAPSIBLE_ROWS = ["SEAT_BASED", "GRADUATED", "VOLUME"];
    if (context === "pdf" && allPricesUseSameStructure) {
      return pricesForProductIdWithPhase.map(({ price, phase }) => {
        const showSeparateTablesForPrice = PRICING_TYPES_WITH_COLLAPSIBLE_ROWS.includes(
          price.structure.pricingType
        );
        const subtitle = (() => {
          if (!showSeparateTablesForPrice) {
            return void 0;
          }
          const {
            dateStartResolved,
            dateEndResolved,
            durationResolved,
            showDateRange
          } = renderPhaseDates(phase.dates);
          return {
            type: "TEXT",
            text: showDateRange ? `${dateStartResolved} to ${dateEndResolved}` : durationResolved ?? ""
          };
        })();
        return {
          title,
          ...caption ? { caption } : {},
          ...subtitle ? { subtitle } : {},
          rows: renderPriceRows({
            pricingType: price.structure.pricingType,
            price,
            phasesWithProductId: [phase],
            /**
             * Don't show phase information in the description column, because it'll be shown in the table subtitle
             */
            descriptionType: showSeparateTablesForPrice ? "PRICE_NAME" : "PHASE_LABEL",
            isCollapsible: false
          })
        };
      });
    }
    return {
      title,
      ...caption ? { caption } : {},
      rows: pricesForProductIdWithPhase.flatMap(
        ({ price, phase }) => renderPriceRows({
          pricingType: price.structure.pricingType,
          price,
          phasesWithProductId: [phase],
          descriptionType: "PHASE_LABEL",
          /**
           * We don't render separate tables for tiered pricing structures (with collapsible rows) in PDFs when
           * there's differences in the pricing types for the prices used by a product, so don't hide them
           */
          isCollapsible: context !== "pdf" || allPricesUseSameStructure
        })
      )
    };
  }).filter(Boolean);
};
var renderPriceRows = ({
  pricingType,
  price,
  phasesWithProductId,
  descriptionType,
  isCollapsible
}) => {
  return match5(pricingType).with(
    "FIXED",
    "ONE_TIME",
    () => renderFixedOneTimePrice({
      price,
      phasesWithProductId,
      descriptionType
    })
  ).with(
    "LINEAR",
    () => renderLinearPrice({
      price,
      phasesWithProductId,
      descriptionType,
      hasCollapsibleMinMaxRates: isCollapsible
    })
  ).with(
    "VOLUME",
    "GRADUATED",
    () => renderVolumeGraduatedPrice({
      price,
      phasesWithProductId,
      descriptionType,
      hasCollapsibleTiers: isCollapsible
    })
  ).with(
    "PACKAGE",
    () => renderPackagePrice({
      price,
      phasesWithProductId,
      descriptionType
    })
  ).with(
    "SEAT_BASED",
    () => renderSeatBasedPrice({
      price,
      phasesWithProductId,
      descriptionType,
      hasCollapsibleTiers: isCollapsible
    })
  ).exhaustive();
};
var getCaption = (price) => {
  return match5(price).with({ structure: { pricingType: "GRADUATED" } }, () => GRADUATED_CAPTION).with({ structure: { pricingType: "VOLUME" } }, () => VOLUME_CAPTION).with(
    { structure: { pricingType: "SEAT_BASED" } },
    ({ structure }) => structure.tiers.length > 0 ? SEAT_BASED_GRADUATED_CAPTION : void 0
  ).with(
    { structure: { pricingType: "FIXED" } },
    { structure: { pricingType: "LINEAR" } },
    { structure: { pricingType: "ONE_TIME" } },
    { structure: { pricingType: "PACKAGE" } },
    () => void 0
  ).exhaustive();
};

// src/renderers/common/usePhaseBlock/renderPhases/renderPhases.ts
import { match as match6 } from "ts-pattern";
var renderPhases = ({
  config
}) => {
  const { phases } = config;
  return phases.map((phase) => {
    const title = (() => {
      if (phase.name) {
        return {
          type: "TEXT",
          text: phase.name
        };
      }
      const { showDateRange, durationResolved } = renderPhaseDates(phase.dates);
      if (showDateRange) {
        return {
          type: "DATE_RANGE",
          dates: phase.dates
        };
      }
      return {
        type: "TEXT",
        text: durationResolved || OPEN_ENDED_LABEL
      };
    })();
    const subtitle = (() => {
      if (phase.name) {
        const { showDateRange, durationResolved } = renderPhaseDates(
          phase.dates
        );
        if (showDateRange) {
          return {
            type: "DATE_RANGE",
            dates: phase.dates
          };
        }
        return {
          type: "TEXT",
          text: durationResolved ?? ""
        };
      }
      return void 0;
    })();
    const rows = phase.prices.flatMap((phasePrice) => {
      return match6(phasePrice).with(
        { structure: { pricingType: "FIXED" } },
        { structure: { pricingType: "ONE_TIME" } },
        (price) => {
          return renderFixedOneTimePrice({
            price,
            phasesWithProductId: [phase],
            descriptionType: "PRICE_NAME"
          });
        }
      ).with({ structure: { pricingType: "LINEAR" } }, (price) => {
        return renderLinearPrice({
          price,
          phasesWithProductId: [phase],
          descriptionType: "PRICE_NAME",
          hasCollapsibleMinMaxRates: false
        });
      }).with(
        { structure: { pricingType: "VOLUME" } },
        { structure: { pricingType: "GRADUATED" } },
        (price) => {
          return renderVolumeGraduatedPrice({
            price,
            phasesWithProductId: [phase],
            descriptionType: "PRICE_NAME",
            hasCollapsibleTiers: false
          });
        }
      ).with({ structure: { pricingType: "PACKAGE" } }, (price) => {
        return renderPackagePrice({
          price,
          phasesWithProductId: [phase],
          descriptionType: "PRICE_NAME"
        });
      }).with({ structure: { pricingType: "SEAT_BASED" } }, (price) => {
        return renderSeatBasedPrice({
          price,
          phasesWithProductId: [phase],
          descriptionType: "PRICE_NAME",
          groupBy: "phase",
          hasCollapsibleTiers: false
        });
      }).exhaustive();
    });
    const minimumsRows = (() => {
      if (phase.minimums.length === 0) {
        return [];
      }
      return renderMinimumRowsForPhase({
        phase,
        showDateInDescription: false
      });
    })();
    return {
      title,
      subtitle,
      rows: [...rows, ...minimumsRows]
    };
  });
};

// src/renderers/common/usePhaseBlock/usePhaseBlock.ts
var usePhaseBlock = ({ config, context, settings }) => {
  const pricingTables = useMemo3(() => {
    if (settings.groupPricesByPhase) {
      return renderPhases({ config }).map((table) => ({
        uuid: uuidv42(),
        ...table
      }));
    }
    return [
      ...renderProducts({ config, context }),
      ...renderMinimums({ config })
    ].map((table) => ({
      uuid: uuidv42(),
      ...table
    }));
  }, [config, context, settings.groupPricesByPhase]);
  const defaultVisibleColumns = useMemo3(() => {
    if (settings.groupPricesByPhase) {
      return ["description", "discount", "rate"];
    }
    return ["description", "quantity", "discount", "rate"];
  }, [settings.groupPricesByPhase]);
  return {
    pricingTables,
    defaultVisibleColumns
  };
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTable.tsx
import { useMemo as useMemo6 } from "react";
import { v4 as uuidv44 } from "uuid";
import { Flex as Flex10 } from "@chakra-ui/react";
import { GreyGrey40 } from "@sequencehq/design-tokens";

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableHeader.tsx
import { Flex as Flex5, Text as Text8 } from "@chakra-ui/react";
import { GreyGrey10, GreyGrey70 as GreyGrey702, Lato13Bold as Lato13Bold3 } from "@sequencehq/design-tokens";
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
var PhaseBlockTableHeader = ({
  bestColumnWidths,
  visibleColumns
}) => {
  const fontStyle = {
    ...Lato13Bold3,
    color: GreyGrey702
  };
  const cellStyle = {
    padding: "9px 12px",
    justifyContent: "flex-end"
  };
  return /* @__PURE__ */ jsxs6(Flex5, { background: GreyGrey10, children: [
    visibleColumns.includes("description") && /* @__PURE__ */ jsx10(Flex5, { ...cellStyle, flex: "1", justifyContent: "flex-start", children: /* @__PURE__ */ jsx10(Text8, { ...fontStyle, children: "Description" }) }),
    visibleColumns.includes("quantity") && /* @__PURE__ */ jsx10(
      Flex5,
      {
        ...cellStyle,
        "data-phase-column-id": "quantity",
        width: bestColumnWidths.quantity,
        children: /* @__PURE__ */ jsx10(Text8, { ...fontStyle, children: "Qty" })
      }
    ),
    visibleColumns.includes("discount") && /* @__PURE__ */ jsx10(
      Flex5,
      {
        ...cellStyle,
        "data-phase-column-id": "discount",
        width: bestColumnWidths.discount,
        children: /* @__PURE__ */ jsx10(Text8, { ...fontStyle, children: "Discount" })
      }
    ),
    visibleColumns.includes("rate") && /* @__PURE__ */ jsx10(
      Flex5,
      {
        ...cellStyle,
        "data-phase-column-id": "rate",
        width: bestColumnWidths.rate,
        children: /* @__PURE__ */ jsx10(Text8, { ...fontStyle, children: "Rate" })
      }
    )
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableRow.tsx
import ChevronUpIcon from "@heroicons/react/16/solid/ChevronUpIcon";
import ChevronDownIcon from "@heroicons/react/16/solid/ChevronDownIcon";
import { Flex as Flex7, Text as Text10 } from "@chakra-ui/react";
import {
  GreyGrey30 as GreyGrey302,
  GreyGrey60 as GreyGrey603,
  GreyGrey80 as GreyGrey807,
  Lato13Bold as Lato13Bold4,
  Lato13Regular as Lato13Regular4
} from "@sequencehq/design-tokens";
import { useCallback, useMemo as useMemo4 } from "react";

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Cells/DateRangeCell.tsx
import ArrowLongRightIcon2 from "@heroicons/react/16/solid/ArrowLongRightIcon";
import { Flex as Flex6, Text as Text9 } from "@chakra-ui/react";
import {
  GreyGrey70 as GreyGrey703,
  GreyGrey80 as GreyGrey806,
  Lato13Regular as Lato13Regular3
} from "@sequencehq/design-tokens";
import { Fragment as Fragment4, jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
var DateRangeCell = ({ dates, additionalText }) => {
  const {
    dateStartResolved,
    dateEndResolved,
    durationResolved,
    showDateRange
  } = renderPhaseDates(dates);
  return /* @__PURE__ */ jsxs7(Flex6, { gap: "4px", flexWrap: "wrap", children: [
    showDateRange && /* @__PURE__ */ jsxs7(Fragment4, { children: [
      /* @__PURE__ */ jsx11(Text9, { ...Lato13Regular3, color: GreyGrey806, children: dateStartResolved }),
      /* @__PURE__ */ jsx11(ArrowLongRightIcon2, { width: "16px", height: "16px", color: GreyGrey703 }),
      /* @__PURE__ */ jsx11(Text9, { ...Lato13Regular3, color: GreyGrey806, children: dateEndResolved }),
      additionalText && /* @__PURE__ */ jsx11(Text9, { ...Lato13Regular3, color: GreyGrey806, children: additionalText })
    ] }),
    durationResolved && !showDateRange && /* @__PURE__ */ jsxs7(Text9, { ...Lato13Regular3, color: GreyGrey806, children: [
      durationResolved,
      additionalText ? ` ${additionalText}` : ""
    ] })
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableRow.tsx
import { match as match7 } from "ts-pattern";
import { Fragment as Fragment5, jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var PhaseBlockTableRow = ({
  row,
  bestColumnWidths,
  isOpen,
  visibleColumns
}) => {
  const cellStyle = {
    display: "flex",
    alignItems: "flex-start",
    padding: "10px 12px",
    justifyContent: "flex-end"
  };
  const isCollapsible = typeof isOpen !== "undefined";
  const renderCellContent = useCallback(
    (cell) => {
      if (!cell) {
        return null;
      }
      return match7(cell).with({ type: "DATE_RANGE" }, (thisCell) => /* @__PURE__ */ jsx12(
        DateRangeCell,
        {
          dates: thisCell.dates,
          additionalText: thisCell.additionalText
        }
      )).with({ type: "TEXT" }, ({ style, text }) => {
        const font = match7(style?.fontWeight).with("regular", void 0, () => Lato13Regular4).with("bold", () => Lato13Bold4).exhaustive();
        const color = match7(style?.color).with("GreyGrey60", () => GreyGrey603).with("GreyGrey80", void 0, () => GreyGrey807).exhaustive();
        const textDecoration = match7(style?.textDecoration).with("line-through", () => "line-through").with(void 0, () => "initial").exhaustive();
        return /* @__PURE__ */ jsx12(Text10, { ...font, color, textDecoration, children: text });
      }).exhaustive();
    },
    []
  );
  const indentation = useMemo4(
    () => match7(row).with({ indentation: 1 }, () => "16px").with({ indentation: 2 }, () => "24px").with({ indentation: 3 }, () => "32px").otherwise(() => "12px"),
    [row]
  );
  return /* @__PURE__ */ jsxs8(Flex7, { borderTop: `${GreyGrey302} 1px solid`, children: [
    visibleColumns.includes("description") && /* @__PURE__ */ jsx12(
      Flex7,
      {
        ...cellStyle,
        flex: "1",
        justifyContent: "flex-start",
        paddingLeft: indentation,
        children: renderCellContent(row.description)
      }
    ),
    visibleColumns.includes("quantity") && /* @__PURE__ */ jsx12(
      Flex7,
      {
        ...cellStyle,
        "data-phase-column-id": "quantity",
        width: bestColumnWidths.quantity,
        children: renderCellContent(row.quantity)
      }
    ),
    visibleColumns.includes("discount") && /* @__PURE__ */ jsx12(
      Flex7,
      {
        ...cellStyle,
        "data-phase-column-id": "discount",
        width: bestColumnWidths.discount,
        children: /* @__PURE__ */ jsx12(Text10, { color: GreyGrey807, ...Lato13Regular4, children: row.discount && renderCellContent(row.discount) })
      }
    ),
    visibleColumns.includes("rate") && /* @__PURE__ */ jsxs8(
      Flex7,
      {
        ...cellStyle,
        "data-phase-column-id": "rate",
        width: bestColumnWidths.rate,
        gap: "6px",
        children: [
          row.discountedRate && renderCellContent(row.discountedRate),
          renderCellContent(row.rate),
          isCollapsible && /* @__PURE__ */ jsx12(Fragment5, { children: isOpen ? /* @__PURE__ */ jsx12(ChevronUpIcon, { width: "16px", height: "16px", color: "inherit" }) : /* @__PURE__ */ jsx12(ChevronDownIcon, { width: "16px", height: "16px", color: "inherit" }) })
        ]
      }
    )
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableSubrowsSection.tsx
import { useMemo as useMemo5 } from "react";
import { v4 as uuidv43 } from "uuid";

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableRowCollapse.tsx
import { useCallback as useCallback2, useState } from "react";
import { Button as Button2, Collapse, Box as Box4 } from "@chakra-ui/react";
import { Fragment as Fragment6, jsx as jsx13, jsxs as jsxs9 } from "react/jsx-runtime";
var PhaseBlockTableRowCollapse = ({
  trigger,
  children,
  defaultIsOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const toggle = useCallback2(() => setIsOpen(!isOpen), [isOpen]);
  return /* @__PURE__ */ jsxs9(Fragment6, { children: [
    /* @__PURE__ */ jsx13(Button2, { onClick: toggle, variant: "unstyled", height: "initial", children: trigger(isOpen) }),
    /* @__PURE__ */ jsx13(Collapse, { in: isOpen, children: /* @__PURE__ */ jsx13(Box4, { children }) })
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableSubrowsSection.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var PhaseBlockTableSubrowsSection = ({
  row,
  defaultIsOpen,
  bestColumnWidths,
  visibleColumns
}) => {
  const subrowsWithUuid = useMemo5(() => {
    if (!row.subrows) {
      return [];
    }
    return row.subrows.map((subrow) => ({
      uuid: uuidv43(),
      ...subrow
    }));
  }, [row.subrows]);
  return /* @__PURE__ */ jsx14(
    PhaseBlockTableRowCollapse,
    {
      trigger: (isOpen) => /* @__PURE__ */ jsx14(
        PhaseBlockTableRow,
        {
          row,
          bestColumnWidths,
          isOpen,
          visibleColumns
        }
      ),
      defaultIsOpen,
      children: subrowsWithUuid.map((subrow) => /* @__PURE__ */ jsx14(
        PhaseBlockTableRow,
        {
          row: subrow,
          bestColumnWidths,
          visibleColumns
        },
        subrow.uuid
      ))
    }
  );
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableCaption.tsx
import { Center as Center2, Flex as Flex8, Text as Text11 } from "@chakra-ui/react";
import { InformationCircleIcon } from "@heroicons/react/16/solid";
import {
  GreyGrey0,
  GreyGrey30 as GreyGrey303,
  GreyGrey60 as GreyGrey604,
  GreyGrey70 as GreyGrey704,
  Lato13Regular as Lato13Regular5
} from "@sequencehq/design-tokens";
import { jsx as jsx15, jsxs as jsxs10 } from "react/jsx-runtime";
var PhaseBlockTableCaption = ({ caption }) => /* @__PURE__ */ jsxs10(
  Flex8,
  {
    background: GreyGrey0,
    borderTop: `${GreyGrey303} 1px solid`,
    paddingX: "12px",
    paddingY: "10px",
    gap: "4px",
    children: [
      /* @__PURE__ */ jsx15(Center2, { flexShrink: "0", children: /* @__PURE__ */ jsx15(InformationCircleIcon, { width: "16px", height: "16px", color: GreyGrey604 }) }),
      /* @__PURE__ */ jsx15(Text11, { ...Lato13Regular5, color: GreyGrey704, children: caption })
    ]
  }
);

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableTitle.tsx
import { match as match8 } from "ts-pattern";
import { Flex as Flex9, Text as Text12 } from "@chakra-ui/react";
import ArrowLongRightIcon3 from "@heroicons/react/16/solid/ArrowLongRightIcon";
import {
  GreyGrey60 as GreyGrey605,
  GreyGrey80 as GreyGrey808,
  Lato13Bold as Lato13Bold5,
  Lato14Bold
} from "@sequencehq/design-tokens";
import { Fragment as Fragment7, jsx as jsx16, jsxs as jsxs11 } from "react/jsx-runtime";
var PhaseBlockTableTitle = ({ pricingTable }) => {
  const { title, subtitle } = pricingTable;
  return /* @__PURE__ */ jsxs11(Flex9, { gap: "8px", alignItems: "baseline", flexWrap: "wrap", children: [
    match8(title).with({ type: "TEXT" }, ({ text }) => /* @__PURE__ */ jsx16(Text12, { ...Lato14Bold, color: GreyGrey808, children: text })).with({ type: "DATE_RANGE" }, ({ dates }) => {
      const { dateStartResolved, dateEndResolved } = renderPhaseDates(dates);
      if (!dateStartResolved || !dateEndResolved) {
        return null;
      }
      return /* @__PURE__ */ jsxs11(Flex9, { gap: "8px", children: [
        /* @__PURE__ */ jsx16(Text12, { ...Lato14Bold, color: GreyGrey808, children: dateStartResolved }),
        /* @__PURE__ */ jsx16(
          ArrowLongRightIcon3,
          {
            width: "16px",
            height: "16px",
            color: GreyGrey808
          }
        ),
        /* @__PURE__ */ jsx16(Text12, { ...Lato14Bold, color: GreyGrey808, children: dateEndResolved })
      ] });
    }).exhaustive(),
    subtitle && /* @__PURE__ */ jsx16(Fragment7, { children: match8(subtitle).with({ type: "TEXT" }, ({ text }) => /* @__PURE__ */ jsx16(Text12, { ...Lato13Bold5, color: GreyGrey605, children: text })).with({ type: "DATE_RANGE" }, ({ dates }) => {
      const { dateStartResolved, dateEndResolved } = renderPhaseDates(dates);
      if (!dateStartResolved || !dateEndResolved) {
        return null;
      }
      return /* @__PURE__ */ jsxs11(Flex9, { gap: "4px", children: [
        /* @__PURE__ */ jsx16(Text12, { ...Lato13Bold5, color: GreyGrey605, children: dateStartResolved }),
        /* @__PURE__ */ jsx16(
          ArrowLongRightIcon3,
          {
            width: "16px",
            height: "16px",
            color: GreyGrey808
          }
        ),
        /* @__PURE__ */ jsx16(Text12, { ...Lato13Bold5, color: GreyGrey605, children: dateEndResolved })
      ] });
    }).exhaustive() })
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTable.tsx
import { jsx as jsx17, jsxs as jsxs12 } from "react/jsx-runtime";
var PhaseBlockTable = ({
  pricingTable,
  bestColumnWidths,
  defaultVisibleColumns
}) => {
  const { caption, rows } = pricingTable;
  const rowsWithUuid = useMemo6(
    () => rows.map((row) => ({
      uuid: uuidv44(),
      ...row
    })),
    [rows]
  );
  const visibleColumns = useMemo6(
    () => filterDefaultVisibleColumns(rowsWithUuid, defaultVisibleColumns),
    [rowsWithUuid, defaultVisibleColumns]
  );
  return /* @__PURE__ */ jsxs12(Flex10, { flexDirection: "column", gap: "8px", children: [
    /* @__PURE__ */ jsx17(PhaseBlockTableTitle, { pricingTable }),
    /* @__PURE__ */ jsxs12(
      Flex10,
      {
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "8px",
        border: `${GreyGrey40} 1px solid`,
        boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.05)",
        children: [
          /* @__PURE__ */ jsx17(
            PhaseBlockTableHeader,
            {
              bestColumnWidths,
              visibleColumns
            }
          ),
          rowsWithUuid.map((row, rowIndex) => {
            if (row.subrows) {
              return /* @__PURE__ */ jsx17(
                PhaseBlockTableSubrowsSection,
                {
                  row,
                  bestColumnWidths,
                  defaultIsOpen: rowIndex === 0,
                  visibleColumns
                },
                row.uuid
              );
            }
            return /* @__PURE__ */ jsx17(
              PhaseBlockTableRow,
              {
                row,
                bestColumnWidths,
                visibleColumns
              },
              row.uuid
            );
          }),
          caption && /* @__PURE__ */ jsx17(PhaseBlockTableCaption, { caption })
        ]
      }
    )
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableContainer.tsx
import { Flex as Flex11 } from "@chakra-ui/react";
import { jsx as jsx18 } from "react/jsx-runtime";
var PhaseBlockTableContainer = ({ children }) => /* @__PURE__ */ jsx18(Flex11, { flexDirection: "column", gap: "24px", children });

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/hooks/useBestColumnWidths.ts
import {
  useState as useState2,
  useLayoutEffect,
  useCallback as useCallback3
} from "react";
var useBestColumnWidths = () => {
  const [quantity, setQuantity] = useState2(void 0);
  const [rate, setRate] = useState2(void 0);
  const [discount, setDiscount] = useState2(void 0);
  const calculateBestColumnWidth = useCallback3(
    ({
      columnId,
      setBestWidth
    }) => {
      const columnValues = document.querySelectorAll(
        `[data-phase-column-id="${columnId}"]`
      );
      const columnValueWidths = Array.from(columnValues).map((columnValue) => columnValue.getBoundingClientRect().width).sort((a, b) => b - a);
      const roundedWidth = Math.ceil(columnValueWidths[0]);
      if (roundedWidth < 50) {
        setBestWidth(`180px`);
        return;
      }
      if (columnValueWidths[0] && columnValueWidths[0] > 0) {
        setBestWidth(`${roundedWidth}px`);
      }
    },
    []
  );
  useLayoutEffect(() => {
    document.fonts.ready.then(() => {
      calculateBestColumnWidth({
        columnId: "quantity",
        setBestWidth: setQuantity
      });
      calculateBestColumnWidth({
        columnId: "discount",
        setBestWidth: setDiscount
      });
      calculateBestColumnWidth({
        columnId: "rate",
        setBestWidth: setRate
      });
    });
  }, [calculateBestColumnWidth]);
  return {
    quantity,
    rate,
    discount
  };
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/PhaseBlockMeta.tsx
import { Flex as Flex12, Text as Text13 } from "@chakra-ui/react";
import { GreyGrey60 as GreyGrey606, Lato13Bold as Lato13Bold6 } from "@sequencehq/design-tokens";
import { toMoney as toMoney3 } from "@sequencehq/core-models";
import { jsx as jsx19, jsxs as jsxs13 } from "react/jsx-runtime";
var PhaseBlockMeta = ({
  meta,
  currency
}) => {
  const shouldShowTotal = Boolean(meta.totalFullRate && meta.totalFullRate > 0);
  if (!shouldShowTotal) {
    return null;
  }
  const shouldShowDiscountedRate = typeof meta.totalDiscountedRate !== "undefined" && typeof meta.totalFullRate !== "undefined" && meta.totalFullRate !== meta.totalDiscountedRate;
  return /* @__PURE__ */ jsx19(Flex12, { width: "100%", justifyContent: "flex-end", paddingTop: "12px", children: /* @__PURE__ */ jsxs13(Text13, { ...Lato13Bold6, children: [
    "Total:",
    " ",
    /* @__PURE__ */ jsx19(
      Text13,
      {
        as: "span",
        textDecoration: shouldShowDiscountedRate ? "line-through" : void 0,
        textColor: shouldShowDiscountedRate ? GreyGrey606 : void 0,
        ...Lato13Bold6,
        children: toMoney3({ currency, value: `${meta.totalFullRate}` })
      }
    ),
    " ",
    shouldShowDiscountedRate && /* @__PURE__ */ jsx19(Text13, { as: "span", ...Lato13Bold6, children: toMoney3({ currency, value: `${meta.totalDiscountedRate}` }) })
  ] }) });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTables.tsx
import { Fragment as Fragment8, jsx as jsx20, jsxs as jsxs14 } from "react/jsx-runtime";
var PhaseBlockTables = ({
  pricingTables,
  currency,
  defaultVisibleColumns,
  meta
}) => {
  const bestColumnWidths = useBestColumnWidths();
  return /* @__PURE__ */ jsxs14(Fragment8, { children: [
    /* @__PURE__ */ jsx20(PhaseBlockTableContainer, { children: pricingTables.map((pricingTable) => /* @__PURE__ */ jsx20(
      PhaseBlockTable,
      {
        pricingTable,
        bestColumnWidths,
        defaultVisibleColumns
      },
      pricingTable.uuid
    )) }),
    meta && /* @__PURE__ */ jsx20(PhaseBlockMeta, { meta, currency })
  ] });
};

// src/renderers/ReactQuoteRenderer/blocks/PhaseBlock/PhaseBlock.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var PhaseBlock = ({ config, settings, meta }) => {
  const { pricingTables, defaultVisibleColumns } = usePhaseBlock({
    config,
    context: "react",
    settings
  });
  const currency = config.phases[0]?.prices[0]?.currency;
  return /* @__PURE__ */ jsx21(
    PhaseBlockTables,
    {
      pricingTables,
      currency,
      defaultVisibleColumns,
      meta
    }
  );
};

// src/renderers/ReactQuoteRenderer/blocks/BulletListBlock/BulletListBlockV2.tsx
import { ListItem as ListItem3, Text as Text14, UnorderedList as UnorderedList2 } from "@chakra-ui/react";
import { GreyGrey80 as GreyGrey809 } from "@sequencehq/design-tokens";
import { jsx as jsx22 } from "react/jsx-runtime";
var BulletListBlockV2 = ({ config: { items }, font }) => /* @__PURE__ */ jsx22(UnorderedList2, { children: items.map((item, index) => /* @__PURE__ */ jsx22(
  ListItem3,
  {
    marginBottom: index !== items.length - 1 ? "3px" : void 0,
    children: /* @__PURE__ */ jsx22(Text14, { ...font, color: GreyGrey809, children: renderText({ text: item, font }) })
  },
  index
)) });

// src/renderers/ReactQuoteRenderer/blocks/NumberListBlock/NumberListBlockV2.tsx
import { ListItem as ListItem4, OrderedList as OrderedList2, Text as Text15 } from "@chakra-ui/react";
import { GreyGrey80 as GreyGrey8010 } from "@sequencehq/design-tokens";
import { jsx as jsx23 } from "react/jsx-runtime";
var NumberListBlockV2 = ({ config: { items }, font }) => /* @__PURE__ */ jsx23(OrderedList2, { children: items.map((item, index) => /* @__PURE__ */ jsx23(
  ListItem4,
  {
    ...font,
    marginBottom: index !== items.length - 1 ? "7px" : void 0,
    children: /* @__PURE__ */ jsx23(Text15, { ...font, color: GreyGrey8010, children: renderText({ text: item, font }) })
  },
  index
)) });

// src/renderers/ReactQuoteRenderer/blocks/AttachmentsBlock/AttachmentsBlock.tsx
import { Flex as Flex13, Text as Text16, Box as Box5, Link as Link2, Icon, Center as Center3 } from "@chakra-ui/react";
import {
  GreyGrey60 as GreyGrey607,
  GreyGrey80 as GreyGrey8011,
  Lato13Regular as Lato13Regular6,
  GreyGrey30 as GreyGrey304,
  Lato14Bold as Lato14Bold2,
  Lato20Bold,
  Lato14Regular,
  GreyGrey20
} from "@sequencehq/design-tokens";
import { DocumentIcon, ArrowDownTrayIcon } from "@heroicons/react/16/solid";
import { formatFileSizeToHumanReadable } from "@sequencehq/formatters";
import { jsx as jsx24, jsxs as jsxs15 } from "react/jsx-runtime";
var AttachmentsBlock = ({ attachments }) => {
  return /* @__PURE__ */ jsxs15(Box5, { children: [
    /* @__PURE__ */ jsx24(AttachmentBlockHeader, { attachments }),
    /* @__PURE__ */ jsxs15(Flex13, { flexDirection: "column", mt: "16px", children: [
      /* @__PURE__ */ jsxs15(Flex13, { px: "4px", py: "6px", children: [
        /* @__PURE__ */ jsx24(Text16, { ...Lato14Bold2, flex: 1, pl: "2px", children: "Filename" }),
        /* @__PURE__ */ jsx24(Text16, { ...Lato14Bold2, pr: 8, children: "Size" })
      ] }),
      attachments.map((attachment, index) => /* @__PURE__ */ jsx24(
        AttachmentBlockItem,
        {
          attachment,
          showBorder: index < attachments.length
        },
        index
      ))
    ] })
  ] });
};
var AttachmentBlockHeader = ({ attachments }) => {
  const totalFileSize = attachments.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.fileSizeBytes;
  }, 0);
  return /* @__PURE__ */ jsxs15(Flex13, { flexDirection: "column", gap: 2, pt: 8, children: [
    /* @__PURE__ */ jsx24(Text16, { ...Lato20Bold, children: "Attachments" }),
    /* @__PURE__ */ jsxs15(Flex13, { gap: "6px", children: [
      /* @__PURE__ */ jsxs15(Text16, { ...Lato14Regular, color: GreyGrey607, children: [
        `${attachments.length} ${attachments.length > 0 ? "files" : "file"}`,
        " "
      ] }),
      /* @__PURE__ */ jsx24(Text16, { ...Lato14Regular, color: GreyGrey607, children: /* @__PURE__ */ jsx24(Box5, { display: "inline-flex", verticalAlign: "top", fontSize: "10px", children: "\u25CF" }) }),
      /* @__PURE__ */ jsx24(Text16, { ...Lato14Regular, color: GreyGrey607, children: `${formatFileSizeToHumanReadable(totalFileSize)}` })
    ] })
  ] });
};
var AttachmentBlockItem = ({
  attachment,
  showBorder
}) => {
  return /* @__PURE__ */ jsx24(
    Link2,
    {
      href: attachment.url,
      target: "_blank",
      rel: "noopener noreferrer",
      style: { textDecoration: "none" },
      _hover: { background: GreyGrey20 },
      role: "group",
      children: /* @__PURE__ */ jsxs15(
        Flex13,
        {
          gap: 2,
          px: "4px",
          py: "6px",
          alignItems: "center",
          borderTop: showBorder ? `${GreyGrey304} 1px solid` : void 0,
          height: "36px",
          children: [
            /* @__PURE__ */ jsxs15(Flex13, { flex: 1, gap: 2, alignItems: "center", children: [
              /* @__PURE__ */ jsx24(Icon, { as: DocumentIcon, width: "16px", color: GreyGrey607 }),
              /* @__PURE__ */ jsx24(
                Text16,
                {
                  ...Lato14Regular,
                  fontWeight: "semibold",
                  color: GreyGrey8011,
                  noOfLines: 1,
                  children: attachment.fileName
                }
              )
            ] }),
            /* @__PURE__ */ jsx24(Text16, { ...Lato13Regular6, color: GreyGrey607, children: formatFileSizeToHumanReadable(attachment.fileSizeBytes) }),
            /* @__PURE__ */ jsx24(Box5, { width: "24px", p: "8px", children: /* @__PURE__ */ jsx24(Center3, { children: /* @__PURE__ */ jsx24(
              Icon,
              {
                as: ArrowDownTrayIcon,
                width: "16px",
                color: GreyGrey607,
                _groupHover: { color: GreyGrey8011 }
              }
            ) }) })
          ]
        }
      )
    }
  );
};

// src/renderers/ReactQuoteRenderer/blocks/CoverImageBlock/CoverImageBlock.tsx
import { Box as Box6 } from "@chakra-ui/react";
import { useGetSignedAssetUrl as useGetSignedAssetUrl2 } from "@sequencehq/utils";
import { jsx as jsx25 } from "react/jsx-runtime";
var CoverImageBlock = ({ config, assetLoader }) => {
  const backgroundImage = useGetSignedAssetUrl2({
    url: config.asset.url,
    assetLoader
  });
  return /* @__PURE__ */ jsx25(
    Box6,
    {
      height: "240px",
      backgroundImage,
      backgroundSize: "100% auto",
      backgroundPosition: "50% 50%",
      backgroundRepeat: "no-repeat"
    }
  );
};

// src/renderers/ReactQuoteRenderer/blocks/BulletListBlock/BulletListBlockV3.tsx
import { ListItem as ListItem5, Text as Text17, UnorderedList as UnorderedList3 } from "@chakra-ui/react";
import { jsx as jsx26 } from "react/jsx-runtime";
var BulletListBlockV3 = ({ config: { items }, font }) => /* @__PURE__ */ jsx26(UnorderedList3, { children: items.map((item, index) => {
  const backgroundColor = mapBackgroundColor(item.backgroundColor);
  const textColor = mapTextColor(item.textColor);
  return /* @__PURE__ */ jsx26(
    ListItem5,
    {
      marginBottom: index !== items.length - 1 ? "3px" : void 0,
      style: { backgroundColor, color: textColor },
      children: /* @__PURE__ */ jsx26(Text17, { ...font, color: textColor, children: renderText({ text: item.text, font }) })
    },
    index
  );
}) });

// src/renderers/ReactQuoteRenderer/blocks/NumberListBlock/NumberListBlockV3.tsx
import { ListItem as ListItem6, OrderedList as OrderedList3, Text as Text18 } from "@chakra-ui/react";
import { jsx as jsx27 } from "react/jsx-runtime";
var NumberListBlockV3 = ({ config: { items }, font }) => /* @__PURE__ */ jsx27(OrderedList3, { children: items.map((item, index) => {
  const backgroundColor = mapBackgroundColor(item.backgroundColor);
  const textColor = mapTextColor(item.textColor);
  return /* @__PURE__ */ jsx27(
    ListItem6,
    {
      ...font,
      marginBottom: index !== items.length - 1 ? "7px" : void 0,
      style: { backgroundColor, color: textColor },
      children: /* @__PURE__ */ jsx27(Text18, { ...font, color: textColor, children: renderText({ text: item.text, font }) })
    },
    index
  );
}) });

// src/renderers/ReactQuoteRenderer/blocks/CoverImageBlock/CoverImageBlockV2.tsx
import { Box as Box7 } from "@chakra-ui/react";
import { useGetSignedAssetUrl as useGetSignedAssetUrl3 } from "@sequencehq/utils";
import { jsx as jsx28 } from "react/jsx-runtime";
var CoverImageBlockV2 = ({ config, assetLoader }) => {
  const backgroundImage = useGetSignedAssetUrl3({
    url: config.assetPath,
    assetLoader
  });
  return /* @__PURE__ */ jsx28(
    Box7,
    {
      height: "240px",
      backgroundImage,
      backgroundSize: "100% auto",
      backgroundPosition: "50% 50%",
      backgroundRepeat: "no-repeat"
    }
  );
};

// src/renderers/ReactQuoteRenderer/ReactQuoteRenderer.tsx
import { jsx as jsx29, jsxs as jsxs16 } from "react/jsx-runtime";
var PRE_HEADER_BLOCK_TYPES = ["coverImage"];
var ReactQuoteRenderer = ({
  data,
  assetLoader
}) => {
  const hook = useReactQuoteRenderer({ data });
  const [preHeaderBlocks, postHeaderBlocks] = partition(
    hook.blocks,
    (block) => PRE_HEADER_BLOCK_TYPES.includes(block.blockType)
  );
  return /* @__PURE__ */ jsxs16(Flex14, { direction: "column", pb: "40px", gap: "40px", width: "100%", children: [
    renderBlocks({
      blocks: preHeaderBlocks,
      settings: hook.settings,
      assetLoader
    }),
    /* @__PURE__ */ jsx29(Flex14, { direction: "column", width: "762px", marginX: "auto", gap: "40px", children: /* @__PURE__ */ jsxs16(Box8, { pt: preHeaderBlocks.length ? 0 : "40px", children: [
      /* @__PURE__ */ jsx29(Header, { hook }),
      /* @__PURE__ */ jsx29(Flex14, { flexDirection: "column", pt: "24px", gap: "6px", children: renderBlocks({
        blocks: postHeaderBlocks,
        settings: hook.settings,
        meta: {
          totalFullRate: data.totalFullRate,
          totalDiscountedRate: data.totalDiscountedRate
        },
        assetLoader
      }) })
    ] }) }),
    data.attachmentAssets && data.attachmentAssets.length > 0 && /* @__PURE__ */ jsx29(Box8, { borderTop: `${GreyGrey305} 1px solid`, mb: 10, children: /* @__PURE__ */ jsx29(Box8, { width: "762px", marginX: "auto", children: /* @__PURE__ */ jsx29(AttachmentsBlock, { attachments: data.attachmentAssets }) }) })
  ] });
};
function renderBlocks({
  blocks,
  settings,
  meta,
  assetLoader
}) {
  return blocks.map(
    (block) => match9(block).with({ blockType: "heading1" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx29(TextBlock, { config, font: Lato20Bold2 }, uuid);
    }).with({ blockType: "heading2" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx29(TextBlock, { config, font: Lato16Bold }, uuid);
    }).with({ blockType: "heading3" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx29(TextBlock, { config, font: Lato14Bold3 }, uuid);
    }).with({ blockType: "paragraph" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx29(TextBlock, { config, font: Lato14ExtendedRegular3 }, uuid);
    }).with({ blockType: "bulletList" }, ({ uuid, config, version }) => {
      if (version === 3 && isListBlockConfigV3(config)) {
        return /* @__PURE__ */ jsx29(
          BulletListBlockV3,
          {
            config,
            font: Lato14ExtendedRegular3
          },
          uuid
        );
      }
      if (version === 2 && isListBlockConfigV2(config)) {
        return /* @__PURE__ */ jsx29(
          BulletListBlockV2,
          {
            config,
            font: Lato14ExtendedRegular3
          },
          uuid
        );
      }
      if (version === 1 && isListBlockConfig(config)) {
        return /* @__PURE__ */ jsx29(BulletListBlock, { config }, uuid);
      }
      return null;
    }).with({ blockType: "numberList" }, ({ uuid, config, version }) => {
      if (version === 3 && isListBlockConfigV3(config)) {
        return /* @__PURE__ */ jsx29(
          NumberListBlockV3,
          {
            config,
            font: Lato14ExtendedRegular3
          },
          uuid
        );
      }
      if (version === 2 && isListBlockConfigV2(config)) {
        return /* @__PURE__ */ jsx29(
          NumberListBlockV2,
          {
            config,
            font: Lato14ExtendedRegular3
          },
          uuid
        );
      }
      if (version === 1 && isListBlockConfig(config)) {
        return /* @__PURE__ */ jsx29(NumberListBlock, { config }, uuid);
      }
      return null;
    }).with({ blockType: "image" }, ({ uuid, config }) => {
      if (!isImageBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx29(ImageBlock, { config, assetLoader }, uuid);
    }).with({ blockType: "phases" }, ({ uuid, config }) => {
      if (!isPhaseBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx29(
        PhaseBlock,
        {
          config,
          settings,
          meta
        },
        uuid
      );
    }).with({ blockType: "coverImage" }, ({ uuid, config, version }) => {
      if (version === 2 && isCoverImageBlockConfigV2(config)) {
        return /* @__PURE__ */ jsx29(
          CoverImageBlockV2,
          {
            config,
            assetLoader
          },
          uuid
        );
      }
      if (version === 1 && isCoverImageBlockConfig(config)) {
        return /* @__PURE__ */ jsx29(
          CoverImageBlock,
          {
            config,
            assetLoader
          },
          uuid
        );
      }
      return null;
    }).exhaustive()
  );
}

// src/renderers/PdfQuoteRenderer/PdfQuoteRenderer.tsx
import { Document } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/pages/PageQuoteContent.tsx
import { View as View21 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/widgets/Header.tsx
import { View as View2, Text as Text20 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/widgets/SummaryTextPair.tsx
import { View, Text as Text19 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/PdfQuoteRenderer.styles.tsx
import { StyleSheet } from "@react-pdf/renderer";
import {
  GreyGrey100 as GreyGrey1002,
  GreyGrey30 as GreyGrey306,
  GreyGrey60 as GreyGrey608,
  GreyGrey70 as GreyGrey705,
  GreyGrey80 as GreyGrey8012,
  GreyWhite
} from "@sequencehq/design-tokens";

// src/renderers/PdfQuoteRenderer/PdfQuoteRenderer.utils.ts
var tokenize = (input) => {
  return input.match(/\w+|\W+/g)?.map((s) => s.replace(/ /g, "\xA0"));
};
var fixDesignTokenForPdf = (props) => {
  const fontSizeNum = Number(props.fontSize.replace("px", ""));
  const lineHeightNum = Number(props.lineHeight.replace("px", ""));
  return {
    ...props,
    fontSize: fontSizeNum,
    lineHeight: lineHeightNum / fontSizeNum
  };
};

// src/renderers/PdfQuoteRenderer/PdfQuoteRenderer.styles.tsx
var Lato19BoldPdf = {
  fontFamily: "Lato",
  fontWeight: 700,
  lineHeight: "28px",
  fontSize: "19px"
};
var Lato13BoldPdf = {
  fontFamily: "Lato",
  fontWeight: 700,
  lineHeight: "16px",
  fontSize: "13px"
};
var Lato12BoldPdf = {
  fontFamily: "Lato",
  fontWeight: 700,
  lineHeight: "16px",
  fontSize: "12px"
};
var Lato10RegularPdf = {
  fontFamily: "Lato",
  fontWeight: 400,
  lineHeight: "14.666px",
  fontSize: "10px"
};
var Lato10BoldPdf = {
  fontFamily: "Lato",
  fontWeight: 700,
  lineHeight: "14.666px",
  fontSize: "10px"
};
var styles2 = StyleSheet.create({
  page: {
    backgroundColor: "white",
    paddingBottom: 89,
    paddingTop: 32
  },
  wrapper: {
    paddingLeft: 24,
    paddingRight: 24
  },
  pageAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: 4
  },
  pageFooter: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    padding: 24
  },
  pageSeparator: {
    height: 1,
    backgroundColor: GreyGrey306
  },
  /**
   * Lato 19 Bold
   */
  lato19BoldGreyGrey100: {
    ...fixDesignTokenForPdf(Lato19BoldPdf),
    color: GreyGrey1002
  },
  /**
   * Lato 13 Bold
   */
  lato13BoldGreyGrey80: {
    ...fixDesignTokenForPdf(Lato13BoldPdf),
    color: GreyGrey8012
  },
  /**
   * Lato 12 Bold
   */
  lato12BoldGreyGrey80: {
    ...fixDesignTokenForPdf(Lato12BoldPdf),
    color: GreyGrey8012
  },
  /**
   * Lato 10 Regular
   */
  lato10RegularGreyGrey100: {
    ...fixDesignTokenForPdf(Lato10RegularPdf),
    color: GreyGrey1002
  },
  lato10RegularGreyGrey80: {
    ...fixDesignTokenForPdf(Lato10RegularPdf),
    color: GreyGrey8012
  },
  lato10RegularGreyGrey70: {
    ...fixDesignTokenForPdf(Lato10RegularPdf),
    color: GreyGrey705
  },
  lato10RegularGreyGrey60: {
    ...fixDesignTokenForPdf(Lato10RegularPdf),
    color: GreyGrey608
  },
  lato10RegularHidden: {
    ...fixDesignTokenForPdf(Lato10RegularPdf),
    color: GreyWhite
  },
  /**
   * Lato 10 Bold
   */
  lato10BoldGreyGrey100: {
    ...fixDesignTokenForPdf(Lato10BoldPdf),
    color: GreyGrey1002
  },
  lato10BoldGreyGrey80: {
    ...fixDesignTokenForPdf(Lato10BoldPdf),
    color: GreyGrey8012
  },
  lato10BoldGreyGrey70: {
    ...fixDesignTokenForPdf(Lato10BoldPdf),
    color: GreyGrey705
  },
  /**
   * Colours
   */
  greyGrey60: {
    color: GreyGrey608
  },
  greyGrey80: {
    color: GreyGrey8012
  },
  /**
   * Formatting
   */
  lineThrough: {
    textDecoration: "line-through",
    textDecorationStyle: "solid",
    textDecorationColor: GreyGrey608
  }
});
var PdfQuoteRenderer_styles_default = styles2;

// src/renderers/PdfQuoteRenderer/widgets/SummaryTextPair.tsx
import { jsx as jsx30, jsxs as jsxs17 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80, lato10BoldGreyGrey80 } = PdfQuoteRenderer_styles_default;
var SummaryTextPair = ({
  title: titleProp,
  value
}) => {
  const title = `${titleProp}:`;
  return /* @__PURE__ */ jsxs17(View, { style: { display: "flex", flexDirection: "row", flexWrap: "wrap" }, children: [
    tokenize(title)?.map((seg, i) => /* @__PURE__ */ jsx30(Text19, { style: lato10BoldGreyGrey80, children: seg }, i)),
    /* @__PURE__ */ jsx30(Text19, { style: lato10BoldGreyGrey80, children: " " }),
    tokenize(value)?.map((seg, i) => /* @__PURE__ */ jsx30(Text19, { style: lato10RegularGreyGrey80, children: seg }, i))
  ] });
};

// src/renderers/PdfQuoteRenderer/widgets/Image.tsx
import { Image as ReactPdfImage } from "@react-pdf/renderer";
import { jsx as jsx31 } from "react/jsx-runtime";
var Image4 = ({ isRenderedInBrowser, src, ...rest }) => {
  const browserHostname = isRenderedInBrowser ? window?.location?.hostname : null;
  const browserHeaders = isImageFromAssetsDomain(
    browserHostname || null,
    src?.toString()
  ) ? (
    /**
     * When loading Sequence-hosted assets via BunnyCDN, we need to make sure that it returns a PNG/JPEG,
     * rather than a WEBP, which isn't supported by react-pdf. This only applies to PDFs generated in the browser,
     * as BunnyCDN appears to just use the uploaded format when requests are made outside the browser.
     */
    { "sequence-skip-optimizer": "true" }
  ) : (
    /**
     * react-pdf seems to cause CORS errors when loading external images from a cache, so add headers to prevent
     * that when rendering in a browser.
     * Source: https://github.com/diegomura/react-pdf/issues/1253#issuecomment-1475861858
     *
     * This header should only be applied if loading external assets, as passing it to requests on BunnyCDN
     * causes the request to fail.
     */
    { "Cache-Control": "no-cache" }
  );
  const imgSrc = isRenderedInBrowser ? {
    uri: src,
    method: "GET",
    headers: browserHeaders,
    body: ""
  } : src;
  return /* @__PURE__ */ jsx31(ReactPdfImage, { src: imgSrc, ...rest });
};
var isImageFromAssetsDomain = (browserHostname, src) => {
  if (!browserHostname || !src) {
    return false;
  }
  const parts = browserHostname.split(".");
  const rootDomain = parts.slice(-2).join(".");
  const srcHostname = new URL(src).hostname;
  return srcHostname.endsWith(rootDomain);
};

// src/renderers/PdfQuoteRenderer/widgets/Header.tsx
import { jsx as jsx32, jsxs as jsxs18 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey802, lato10BoldGreyGrey80: lato10BoldGreyGrey802, lato19BoldGreyGrey100 } = PdfQuoteRenderer_styles_default;
var Header2 = ({ hook }) => {
  return /* @__PURE__ */ jsxs18(View2, { style: { gap: "16px" }, children: [
    /* @__PURE__ */ jsxs18(
      View2,
      {
        style: {
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          justifyContent: "space-between"
        },
        children: [
          /* @__PURE__ */ jsx32(Text20, { style: [lato19BoldGreyGrey100, { maxWidth: "80%" }], children: hook.title }),
          hook.merchantLogoUrl && /* @__PURE__ */ jsx32(
            Image4,
            {
              src: hook.merchantLogoUrl,
              isRenderedInBrowser: hook.isRenderedInBrowser,
              style: { height: "16px" }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs18(View2, { style: { display: "flex", flexDirection: "row", gap: "8px" }, children: [
      /* @__PURE__ */ jsxs18(View2, { style: { flex: "1", display: "flex", gap: "16px" }, children: [
        /* @__PURE__ */ jsxs18(View2, { children: [
          hook.contractLength && /* @__PURE__ */ jsx32(
            SummaryTextPair,
            {
              title: "Contract length",
              value: hook.contractLength
            }
          ),
          hook.startDate && /* @__PURE__ */ jsx32(SummaryTextPair, { title: "Start date", value: hook.startDate }),
          /* @__PURE__ */ jsx32(
            SummaryTextPair,
            {
              title: "Issue date",
              value: hook.issueDate || "Unissued"
            }
          ),
          hook.validUntilDate && /* @__PURE__ */ jsx32(
            SummaryTextPair,
            {
              title: "Valid until",
              value: hook.validUntilDate
            }
          ),
          /* @__PURE__ */ jsx32(SummaryTextPair, { title: "Type", value: hook.dealType })
        ] }),
        hook.prospectName && hook.prospectAddress && /* @__PURE__ */ jsxs18(View2, { children: [
          /* @__PURE__ */ jsx32(Text20, { style: lato10BoldGreyGrey802, children: "Quote for" }),
          /* @__PURE__ */ jsx32(Text20, { style: lato10RegularGreyGrey802, children: hook.prospectName }),
          hook.prospectAddress.map((line, index) => /* @__PURE__ */ jsx32(Text20, { style: lato10RegularGreyGrey802, children: line }, index))
        ] })
      ] }),
      /* @__PURE__ */ jsxs18(View2, { style: { flex: "1", textAlign: "right" }, children: [
        /* @__PURE__ */ jsx32(Text20, { style: lato10BoldGreyGrey802, children: hook.merchantName }),
        hook.merchantAddress.map((line, index) => /* @__PURE__ */ jsx32(Text20, { style: lato10RegularGreyGrey802, children: line }, index)),
        /* @__PURE__ */ jsx32(Text20, { style: lato10RegularGreyGrey802, children: hook.merchantEmailAddress })
      ] })
    ] })
  ] });
};

// src/renderers/PdfQuoteRenderer/layout/PageLayout.tsx
import { Page, View as View5 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/PdfQuoteRenderer.constants.ts
var PAGE_SIZE = "LETTER";
var PAGE_DPI = 72;

// src/renderers/PdfQuoteRenderer/layout/PageFooter.tsx
import { View as View3, Text as Text21 } from "@react-pdf/renderer";
import { jsx as jsx33, jsxs as jsxs19 } from "react/jsx-runtime";
var { pageFooter, pageSeparator, lato10RegularGreyGrey80: lato10RegularGreyGrey803 } = PdfQuoteRenderer_styles_default;
var PageFooter = ({ hook }) => /* @__PURE__ */ jsx33(View3, { style: pageFooter, fixed: true, children: /* @__PURE__ */ jsxs19(View3, { style: { display: "flex", gap: 24, justifyContent: "flex-end" }, children: [
  /* @__PURE__ */ jsx33(View3, { style: pageSeparator }),
  /* @__PURE__ */ jsxs19(
    View3,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
        gap: 8
      },
      children: [
        /* @__PURE__ */ jsx33(
          Text21,
          {
            style: {
              ...lato10RegularGreyGrey803,
              flex: 1,
              height: 15,
              overflow: "hidden"
            },
            children: hook.footerText
          }
        ),
        /* @__PURE__ */ jsx33(
          Text21,
          {
            style: {
              ...lato10RegularGreyGrey803,
              width: 60,
              textAlign: "right"
            },
            render: ({ totalPages, pageNumber }) => `Page ${pageNumber} of ${totalPages}`
          }
        )
      ]
    }
  )
] }) });

// src/renderers/PdfQuoteRenderer/layout/PageAccent.tsx
import { View as View4 } from "@react-pdf/renderer";
import { jsx as jsx34 } from "react/jsx-runtime";
var { pageAccent } = PdfQuoteRenderer_styles_default;
var PageAccent = ({ hook }) => /* @__PURE__ */ jsx34(
  View4,
  {
    style: {
      ...pageAccent,
      backgroundColor: hook.brandColour ?? "transparent"
    },
    fixed: true
  }
);

// src/renderers/PdfQuoteRenderer/layout/PageLayout.tsx
import { jsx as jsx35, jsxs as jsxs20 } from "react/jsx-runtime";
var { page, wrapper } = PdfQuoteRenderer_styles_default;
var PageLayout = ({ hook, children }) => /* @__PURE__ */ jsxs20(Page, { size: PAGE_SIZE, dpi: PAGE_DPI, style: page, wrap: true, children: [
  /* @__PURE__ */ jsx35(PageAccent, { hook }),
  /* @__PURE__ */ jsx35(View5, { style: wrapper, children }),
  /* @__PURE__ */ jsx35(PageFooter, { hook })
] });

// src/renderers/PdfQuoteRenderer/blocks/Blocks.tsx
import { useMemo as useMemo10 } from "react";
import { match as match13 } from "ts-pattern";
import { v4 as uuidv46 } from "uuid";
import { View as View20 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/blocks/TextBlock/TextBlock.tsx
import { useMemo as useMemo7 } from "react";
import { match as match10 } from "ts-pattern";
import { View as View7 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/formatting/formatting.tsx
import { Text as Text22, Link as Link3, View as View6 } from "@react-pdf/renderer";
import { jsx as jsx36 } from "react/jsx-runtime";
var renderText2 = ({
  text,
  blockTextColor,
  font,
  blockBackgroundColor
}) => {
  const textArray = Array.isArray(text) ? text : [text];
  return textArray.filter((textItem) => Boolean(textItem)).map((textItem, index) => {
    if (typeof textItem === "string") {
      return /* @__PURE__ */ jsx36(
        Text22,
        {
          style: {
            ...font,
            color: blockTextColor,
            backgroundColor: blockBackgroundColor
          },
          children: textItem
        },
        index
      );
    }
    return renderEnrichedText2({ font, blockTextColor, blockBackgroundColor })(
      textItem,
      index
    );
  });
};
var mapStyles2 = (styles3, blockTextColor, font, blockBackgroundColor) => {
  return {
    fontWeight: styles3.bold || font.fontWeight > 400 ? "bold" : "normal",
    fontStyle: styles3.italic ? "italic" : "normal",
    textDecoration: (() => {
      if (styles3.underline && styles3.strike) {
        return "line-through underline";
      }
      return styles3.underline ? "underline" : styles3.strike ? "line-through" : "none";
    })(),
    color: styles3.textColor ? mapTextColor(styles3.textColor) : blockTextColor,
    backgroundColor: styles3.backgroundColor ? mapBackgroundColor(styles3.backgroundColor) : blockBackgroundColor
  };
};
var renderEnrichedText2 = ({
  font,
  blockTextColor,
  blockBackgroundColor
}) => (textItem, index) => {
  if (textItem.href) {
    return /* @__PURE__ */ jsx36(
      Link3,
      {
        style: {
          ...font,
          ...mapStyles2(
            textItem.styles,
            blockTextColor,
            font,
            blockBackgroundColor
          ),
          color: "#716fff"
        },
        href: (
          /**
           * It's highly unlikely the user intends to have a link
           * to another part of the customer portal, so let's assume
           * a lack of http or https means they still actually intended
           * to go to a different website.
           */
          textItem.href.includes("http") ? textItem.href : `https://${textItem.href}`
        ),
        children: textItem.text
      },
      index
    );
  }
  return tokenize(textItem.text)?.map((seg, i) => {
    if (seg.includes("\n")) {
      const breaks = seg.split("\n").length - 2;
      return /* @__PURE__ */ jsx36(View6, { style: { width: "100%", height: font.fontSize * breaks } });
    }
    return /* @__PURE__ */ jsx36(
      Text22,
      {
        style: {
          ...font,
          ...mapStyles2(
            textItem.styles,
            blockTextColor,
            font,
            blockBackgroundColor
          )
        },
        children: seg
      },
      i
    );
  });
};

// src/renderers/PdfQuoteRenderer/blocks/TextBlock/TextBlock.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var TextBlock2 = ({ config, font }) => {
  const textAlignment = config.textAlignment ?? "left";
  const justifyContent = useMemo7(() => {
    return match10(textAlignment).with("left", () => "flex-start").with("center", () => "center").with("right", () => "flex-end").with("justify", () => "space-between").exhaustive();
  }, [textAlignment]);
  const blockTextColor = mapTextColor(config.textColor);
  const blockBackgroundColor = mapBackgroundColor(
    config.backgroundColor
  );
  const renderedText = renderText2({
    text: config.text,
    blockTextColor,
    font,
    blockBackgroundColor
  });
  if (!renderedText) {
    return null;
  }
  return /* @__PURE__ */ jsx37(
    View7,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent,
        minWidth: "1px",
        minHeight: `${font.fontSize}px`
      },
      children: renderedText
    }
  );
};

// src/renderers/PdfQuoteRenderer/blocks/ImageBlock/ImageBlock.tsx
import { View as View8 } from "@react-pdf/renderer";

// src/renderers/PdfQuoteRenderer/blocks/ImageBlock/calculateImageBlockHeight.ts
var MAX_HEIGHT = 300;
var MAX_WIDTH = 564;
var calculateImageBlockHeight = ({
  height,
  width
}) => {
  if (typeof height === "undefined" || typeof width === "undefined") {
    return MAX_HEIGHT;
  }
  if (width > height) {
    if (height < MAX_HEIGHT && width <= MAX_WIDTH) {
      return height;
    }
    const ratio = width / height;
    const scaledHeight = MAX_WIDTH / ratio;
    if (scaledHeight < MAX_HEIGHT) {
      return scaledHeight;
    }
    return MAX_HEIGHT;
  } else {
    return height > MAX_HEIGHT ? MAX_HEIGHT : height;
  }
};

// src/renderers/PdfQuoteRenderer/blocks/ImageBlock/ImageBlock.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
function applyUserDefinedWidth(imageData, userDefinedWidth) {
  if (!imageData.width || !imageData.height) {
    return imageData;
  }
  const ratio = imageData.width / userDefinedWidth;
  return {
    ...imageData,
    width: userDefinedWidth,
    height: imageData.height / ratio
  };
}
var ImageBlock2 = ({
  config: { src, width: userDefinedWidth },
  isRenderedInBrowser,
  imageData
}) => {
  const thisImageData = imageData.find((data) => data.urlUnsigned === src);
  if (!thisImageData) {
    return null;
  }
  const imageDimensions = applyUserDefinedWidth(thisImageData, userDefinedWidth);
  const height = calculateImageBlockHeight({
    height: imageDimensions.height,
    width: imageDimensions.width
  });
  return /* @__PURE__ */ jsx38(View8, { style: { height }, wrap: false, children: /* @__PURE__ */ jsx38(
    Image4,
    {
      src: thisImageData.urlSigned,
      isRenderedInBrowser,
      style: {
        width: "100%",
        height: "100%",
        objectFit: "contain"
      }
    }
  ) });
};

// src/renderers/PdfQuoteRenderer/blocks/BulletListBlock/BulletListBlock.tsx
import { View as View9, Text as Text23 } from "@react-pdf/renderer";
import { jsx as jsx39, jsxs as jsxs21 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey804 } = PdfQuoteRenderer_styles_default;
var BulletListBlock2 = ({ config: { items } }) => /* @__PURE__ */ jsx39(View9, { style: { display: "flex", gap: "3px" }, children: items.map((item) => /* @__PURE__ */ jsxs21(
  View9,
  {
    style: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: "8px"
    },
    children: [
      /* @__PURE__ */ jsx39(Text23, { style: lato10RegularGreyGrey804, children: "\u2022" }),
      /* @__PURE__ */ jsx39(Text23, { style: { ...lato10RegularGreyGrey804, flex: "1" }, children: item })
    ]
  },
  item
)) });

// src/renderers/PdfQuoteRenderer/blocks/NumberListBlock/NumberListBlock.tsx
import { View as View10, Text as Text24 } from "@react-pdf/renderer";
import { jsx as jsx40, jsxs as jsxs22 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey805 } = PdfQuoteRenderer_styles_default;
var estimateOrdinalWidth = (numberOfItems) => {
  const maxOrdinal = `${String(numberOfItems + 1)}`;
  return `${maxOrdinal.length * 10}px`;
};
var NumberListBlock2 = ({ config: { items } }) => /* @__PURE__ */ jsx40(View10, { style: { display: "flex", gap: "4.5px" }, children: items.map((item, index) => {
  const ordinal = `${String(index + 1)}.`;
  return /* @__PURE__ */ jsxs22(
    View10,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "8px"
      },
      children: [
        /* @__PURE__ */ jsx40(
          Text24,
          {
            style: {
              ...lato10RegularGreyGrey805,
              width: estimateOrdinalWidth(items.length)
            },
            children: ordinal
          }
        ),
        /* @__PURE__ */ jsx40(Text24, { style: { ...lato10RegularGreyGrey805, flex: "1" }, children: item })
      ]
    },
    item
  );
}) });

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTable.tsx
import { useMemo as useMemo9 } from "react";
import { match as match12 } from "ts-pattern";
import { v4 as uuidv45 } from "uuid";
import { View as View13, Text as Text28 } from "@react-pdf/renderer";
import { GreyGrey100 as GreyGrey1003, GreyGrey30 as GreyGrey308, GreyGrey70 as GreyGrey706 } from "@sequencehq/design-tokens";

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableHeader.tsx
import { useCallback as useCallback4 } from "react";
import { View as View11, Text as Text25 } from "@react-pdf/renderer";
import { GreyGrey50 } from "@sequencehq/design-tokens";

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/PhaseBlock.constants.ts
var DEFAULT_COLUMN_PADDING = {
  quantity: { left: 16, right: 8 },
  discount: { left: 8, right: 8 },
  discountedRate: { left: 0, right: 0 },
  rate: { left: 8, right: 0 }
};
var DEFAULT_COLUMN_WIDTHS = {
  quantity: 17 + DEFAULT_COLUMN_PADDING.quantity.left + DEFAULT_COLUMN_PADDING.quantity.right,
  discount: 41 + DEFAULT_COLUMN_PADDING.discount.left + DEFAULT_COLUMN_PADDING.discount.right,
  discountedRate: 0,
  rate: 21 + DEFAULT_COLUMN_PADDING.rate.left + DEFAULT_COLUMN_PADDING.rate.right
};
var RATE_DISCOUNTED_RATE_GAP_PX = 4;

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableHeader.tsx
import { jsx as jsx41, jsxs as jsxs23 } from "react/jsx-runtime";
var { lato10RegularGreyGrey60 } = PdfQuoteRenderer_styles_default;
var PhaseBlockTableHeader2 = ({
  bestColumnWidths,
  visibleColumns
}) => {
  const fontStyle = { ...lato10RegularGreyGrey60 };
  const cellStyle = {
    paddingTop: "10px",
    paddingBottom: "10px",
    textAlign: "right"
  };
  const parsePadding = useCallback4(
    ({ left, right }) => ({
      paddingLeft: `${left}px`,
      paddingRight: `${right}px`
    }),
    []
  );
  return /* @__PURE__ */ jsxs23(
    View11,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        borderBottomColor: GreyGrey50,
        borderBottomStyle: "solid",
        borderBottomWidth: "1px"
      },
      children: [
        visibleColumns.includes("description") && /* @__PURE__ */ jsx41(
          View11,
          {
            style: {
              ...cellStyle,
              paddingLeft: "0px",
              flex: "1",
              textAlign: "left"
            },
            children: /* @__PURE__ */ jsx41(Text25, { style: fontStyle, children: "Description" })
          }
        ),
        visibleColumns.includes("quantity") && /* @__PURE__ */ jsx41(
          View11,
          {
            style: {
              ...cellStyle,
              ...parsePadding(DEFAULT_COLUMN_PADDING["quantity"]),
              width: bestColumnWidths.quantity
            },
            children: /* @__PURE__ */ jsx41(Text25, { style: fontStyle, children: "Qty" })
          }
        ),
        visibleColumns.includes("discount") && /* @__PURE__ */ jsx41(
          View11,
          {
            style: {
              ...cellStyle,
              ...parsePadding(DEFAULT_COLUMN_PADDING["discount"]),
              width: bestColumnWidths.discount
            },
            children: /* @__PURE__ */ jsx41(Text25, { style: fontStyle, children: "Discount" })
          }
        ),
        visibleColumns.includes("rate") && /* @__PURE__ */ jsx41(
          View11,
          {
            style: {
              ...cellStyle,
              ...parsePadding(DEFAULT_COLUMN_PADDING["rate"]),
              width: bestColumnWidths.rate
            },
            children: /* @__PURE__ */ jsx41(Text25, { style: fontStyle, children: "Rate" })
          }
        )
      ]
    }
  );
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableRow.tsx
import { useCallback as useCallback5, useMemo as useMemo8 } from "react";
import { View as View12, Text as Text27 } from "@react-pdf/renderer";
import { match as match11 } from "ts-pattern";
import { GreyGrey30 as GreyGrey307 } from "@sequencehq/design-tokens";

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Cells/DateRangeCell.tsx
import { Text as Text26 } from "@react-pdf/renderer";
import { jsxs as jsxs24 } from "react/jsx-runtime";
var { lato10RegularGreyGrey100 } = PdfQuoteRenderer_styles_default;
var renderDate = (args) => {
  if (args.showDateRange) {
    if (args.isOpenEnded) {
      return `From ${args.dateStartResolved} (${OPEN_ENDED_LABEL})`;
    }
    return `${args.dateStartResolved} to ${args.dateEndResolved}`;
  }
  if (args.durationResolved) {
    return args.durationResolved;
  }
  return "";
};
var DateRangeCell2 = ({ dates, additionalText }) => {
  const {
    dateStartResolved,
    dateEndResolved,
    durationResolved,
    showDateRange,
    isOpenEnded
  } = renderPhaseDates(dates);
  return /* @__PURE__ */ jsxs24(Text26, { style: lato10RegularGreyGrey100, children: [
    renderDate({
      showDateRange,
      isOpenEnded,
      durationResolved,
      dateStartResolved,
      dateEndResolved
    }),
    additionalText ? ` ${additionalText}` : ""
  ] });
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableRow.tsx
import { jsx as jsx42, jsxs as jsxs25 } from "react/jsx-runtime";
var { lato10RegularGreyGrey100: lato10RegularGreyGrey1002, lato10BoldGreyGrey100 } = PdfQuoteRenderer_styles_default;
var PhaseBlockTableRow2 = ({
  row,
  bestColumnWidths,
  showBorderBottom = true,
  visibleColumns
}) => {
  const cellStyle = {
    paddingTop: "10px",
    paddingBottom: "10px",
    textAlign: "right"
  };
  const renderCellContent = useCallback5(
    (cell) => {
      if (!cell) {
        return null;
      }
      return match11(cell).with({ type: "DATE_RANGE" }, (thisCell) => /* @__PURE__ */ jsx42(
        DateRangeCell2,
        {
          dates: thisCell.dates,
          additionalText: thisCell.additionalText
        }
      )).with({ type: "TEXT" }, ({ style, text }) => {
        const stylesProp = [];
        match11(style?.fontWeight).with(
          "regular",
          void 0,
          () => stylesProp.push(lato10RegularGreyGrey1002)
        ).with("bold", () => stylesProp.push(lato10BoldGreyGrey100)).exhaustive();
        match11(style?.color).with("GreyGrey60", () => stylesProp.push(PdfQuoteRenderer_styles_default.greyGrey60)).with(
          "GreyGrey80",
          void 0,
          () => stylesProp.push(PdfQuoteRenderer_styles_default.greyGrey80)
        ).exhaustive();
        if (style?.textDecoration === "line-through") {
          stylesProp.push(PdfQuoteRenderer_styles_default.lineThrough);
          stylesProp.push(PdfQuoteRenderer_styles_default.lato10RegularGreyGrey60);
        }
        return /* @__PURE__ */ jsx42(Text27, { style: stylesProp, children: text });
      }).exhaustive();
    },
    []
  );
  const indentation = useMemo8(
    () => match11(row).with({ indentation: 1 }, () => "8px").with({ indentation: 2 }, () => "16px").with({ indentation: 3 }, () => "24px").otherwise(() => "0px"),
    [row]
  );
  const parsePadding = useCallback5(
    ({ left, right }) => ({
      paddingLeft: `${left}px`,
      paddingRight: `${right}px`
    }),
    []
  );
  return /* @__PURE__ */ jsxs25(
    View12,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        ...showBorderBottom ? {
          borderBottomColor: GreyGrey307,
          borderBottomStyle: "solid",
          borderBottomWidth: "1px"
        } : {}
      },
      wrap: false,
      children: [
        visibleColumns.includes("description") && /* @__PURE__ */ jsx42(
          View12,
          {
            style: {
              ...cellStyle,
              paddingLeft: indentation,
              flex: "1",
              textAlign: "left"
            },
            children: renderCellContent(row.description)
          }
        ),
        visibleColumns.includes("quantity") && /* @__PURE__ */ jsx42(
          View12,
          {
            style: {
              ...cellStyle,
              ...parsePadding(DEFAULT_COLUMN_PADDING["quantity"]),
              width: bestColumnWidths.quantity
            },
            children: renderCellContent(row.quantity)
          }
        ),
        visibleColumns.includes("discount") && /* @__PURE__ */ jsx42(
          View12,
          {
            style: {
              ...cellStyle,
              ...parsePadding(DEFAULT_COLUMN_PADDING["discount"]),
              width: bestColumnWidths.discount
            },
            children: row.discount && renderCellContent(row.discount)
          }
        ),
        visibleColumns.includes("rate") && /* @__PURE__ */ jsxs25(
          View12,
          {
            style: {
              ...cellStyle,
              ...parsePadding(DEFAULT_COLUMN_PADDING["rate"]),
              width: bestColumnWidths.rate,
              display: "flex",
              flexDirection: "row",
              gap: `${RATE_DISCOUNTED_RATE_GAP_PX}px`,
              justifyContent: "flex-end"
            },
            children: [
              row.discountedRate && renderCellContent(row.discountedRate),
              renderCellContent(row.rate)
            ]
          }
        )
      ]
    }
  );
};

// src/renderers/PdfQuoteRenderer/icons/ArrowLongRightIcon.tsx
import { Path, Svg } from "@react-pdf/renderer";
import { jsx as jsx43 } from "react/jsx-runtime";
var ArrowLongRightIcon4 = ({ boxSize, fill, style }) => /* @__PURE__ */ jsx43(
  Svg,
  {
    viewBox: "0 0 16 16",
    fill,
    width: boxSize,
    height: boxSize,
    style,
    children: /* @__PURE__ */ jsx43(
      Path,
      {
        fillRule: "evenodd",
        d: "M2 8c0 .414.336.75.75.75h8.69l-1.22 1.22a.75.75 0 1 0 1.06 1.06l2.5-2.5a.75.75 0 0 0 0-1.06l-2.5-2.5a.75.75 0 1 0-1.06 1.06l1.22 1.22H2.75A.75.75 0 0 0 2 8Z"
      }
    )
  }
);

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTable.tsx
import { Fragment as Fragment9, jsx as jsx44, jsxs as jsxs26 } from "react/jsx-runtime";
var {
  lato10BoldGreyGrey100: lato10BoldGreyGrey1002,
  lato10RegularGreyGrey70,
  lato10RegularGreyGrey60: lato10RegularGreyGrey602
} = PdfQuoteRenderer_styles_default;
var PhaseBlockTable2 = ({
  pricingTable,
  bestColumnWidths,
  defaultVisibleColumns
}) => {
  const { title, subtitle, caption, rows } = pricingTable;
  const rowsWithUuid = useMemo9(
    () => rows.map((row) => ({
      uuid: uuidv45(),
      ...row
    })),
    [rows]
  );
  const visibleColumns = useMemo9(
    () => filterDefaultVisibleColumns(rowsWithUuid, defaultVisibleColumns),
    [rowsWithUuid, defaultVisibleColumns]
  );
  return /* @__PURE__ */ jsxs26(View13, { style: { display: "flex", flexDirection: "column" }, children: [
    /* @__PURE__ */ jsxs26(
      View13,
      {
        style: {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "baseline"
        },
        children: [
          match12(title).with(
            { type: "TEXT" },
            ({ text }) => tokenize(text)?.map((seg, i) => /* @__PURE__ */ jsx44(Text28, { style: lato10BoldGreyGrey1002, children: seg }, i))
          ).with({ type: "DATE_RANGE" }, ({ dates }) => {
            const { dateStartResolved, dateEndResolved } = renderPhaseDates(dates);
            if (!dateStartResolved || !dateEndResolved) {
              return null;
            }
            return /* @__PURE__ */ jsxs26(
              View13,
              {
                style: {
                  display: "flex",
                  gap: "4px",
                  flexDirection: "row",
                  alignItems: "center"
                },
                children: [
                  /* @__PURE__ */ jsx44(Text28, { style: lato10BoldGreyGrey1002, children: dateStartResolved }),
                  /* @__PURE__ */ jsx44(
                    ArrowLongRightIcon4,
                    {
                      boxSize: "16px",
                      fill: GreyGrey1003,
                      style: { position: "relative", top: "-1.25px" }
                    }
                  ),
                  /* @__PURE__ */ jsx44(Text28, { style: lato10BoldGreyGrey1002, children: dateEndResolved })
                ]
              }
            );
          }).exhaustive(),
          subtitle && /* @__PURE__ */ jsxs26(Fragment9, { children: [
            /* @__PURE__ */ jsx44(View13, { style: { height: "11px", width: "10px" } }),
            match12(subtitle).with(
              { type: "TEXT" },
              ({ text }) => tokenize(text)?.map((seg, i) => /* @__PURE__ */ jsx44(Text28, { style: lato10RegularGreyGrey70, children: seg }, i))
            ).with({ type: "DATE_RANGE" }, ({ dates }) => {
              const { dateStartResolved, dateEndResolved } = renderPhaseDates(dates);
              if (!dateStartResolved || !dateEndResolved) {
                return null;
              }
              return /* @__PURE__ */ jsxs26(
                View13,
                {
                  style: {
                    display: "flex",
                    gap: "4px",
                    flexDirection: "row",
                    alignItems: "center"
                  },
                  children: [
                    /* @__PURE__ */ jsx44(Text28, { style: lato10RegularGreyGrey70, children: dateStartResolved }),
                    /* @__PURE__ */ jsx44(
                      ArrowLongRightIcon4,
                      {
                        boxSize: "16px",
                        fill: GreyGrey706,
                        style: { position: "relative", top: "-1.25px" }
                      }
                    ),
                    /* @__PURE__ */ jsx44(Text28, { style: lato10RegularGreyGrey70, children: dateEndResolved })
                  ]
                }
              );
            }).exhaustive()
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs26(View13, { style: { display: "flex", flexDirection: "column" }, children: [
      /* @__PURE__ */ jsx44(
        PhaseBlockTableHeader2,
        {
          bestColumnWidths,
          visibleColumns
        }
      ),
      rowsWithUuid.map((row, rowIndex) => /* @__PURE__ */ jsx44(
        PhaseBlockTableRow2,
        {
          row,
          bestColumnWidths,
          showBorderBottom: rowIndex !== rowsWithUuid.length - 1,
          visibleColumns
        },
        row.uuid
      ))
    ] }),
    caption && /* @__PURE__ */ jsx44(
      View13,
      {
        style: {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          paddingTop: "10px",
          paddingBottom: "10px",
          borderTopColor: GreyGrey308,
          borderTopStyle: "solid",
          borderTopWidth: "1px"
        },
        children: tokenize(caption)?.map((seg, i) => /* @__PURE__ */ jsx44(Text28, { style: lato10RegularGreyGrey602, children: seg }, i))
      }
    )
  ] });
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTableContainer.tsx
import { View as View14 } from "@react-pdf/renderer";
import { jsx as jsx45 } from "react/jsx-runtime";
var PhaseBlockTableContainer2 = ({ children }) => /* @__PURE__ */ jsx45(View14, { style: { display: "flex", flexDirection: "column", gap: "16px" }, children });

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/hooks/fontMetrics/Lato10Bold.ts
var Lato10BoldMetrics = {
  "13": 0,
  "32": 1.93,
  "33": 3.605,
  "34": 4.22,
  "35": 5.8,
  "36": 5.8,
  "37": 8.03,
  "38": 7.140000000000001,
  "39": 2.38,
  "40": 3,
  "41": 3,
  "42": 4,
  "43": 5.8,
  "44": 2.25,
  "45": 3.59,
  "46": 2.25,
  "47": 3.87,
  "48": 5.8,
  "49": 5.8,
  "50": 5.8,
  "51": 5.8,
  "52": 5.8,
  "53": 5.8,
  "54": 5.8,
  "55": 5.8,
  "56": 5.8,
  "57": 5.8,
  "58": 2.65,
  "59": 2.65,
  "60": 5.8,
  "61": 5.8,
  "62": 5.8,
  "63": 4.205,
  "64": 8.225,
  "65": 7.1000000000000005,
  "66": 6.515000000000001,
  "67": 6.705,
  "68": 7.4750000000000005,
  "69": 5.7250000000000005,
  "70": 5.615,
  "71": 7.23,
  "72": 7.5600000000000005,
  "73": 3.14,
  "74": 4.37,
  "75": 6.98,
  "76": 5.17,
  "77": 9.3,
  "78": 7.5600000000000005,
  "79": 7.995,
  "80": 6.25,
  "81": 7.995,
  "82": 6.55,
  "83": 5.365,
  "84": 5.95,
  "85": 7.28,
  "86": 7.1000000000000005,
  "87": 10.465,
  "88": 6.79,
  "89": 6.545,
  "90": 6.17,
  "91": 3,
  "92": 3.9050000000000002,
  "93": 3,
  "94": 5.8,
  "95": 3.94,
  "96": 3.19,
  "97": 5.235,
  "98": 5.7,
  "99": 4.7700000000000005,
  "100": 5.7,
  "101": 5.345,
  "102": 3.505,
  "103": 5.175,
  "104": 5.6850000000000005,
  "105": 2.71,
  "106": 2.69,
  "107": 5.515,
  "108": 2.71,
  "109": 8.42,
  "110": 5.6850000000000005,
  "111": 5.6850000000000005,
  "112": 5.655,
  "113": 5.7,
  "114": 4.085,
  "115": 4.37,
  "116": 3.87,
  "117": 5.6850000000000005,
  "118": 5.335,
  "119": 7.91,
  "120": 5.4,
  "121": 5.335,
  "122": 4.695,
  "123": 3,
  "124": 3,
  "125": 3,
  "126": 5.8,
  "160": 1.93,
  "161": 3.605,
  "162": 5.8,
  "163": 5.8,
  "164": 5.8,
  "165": 5.8,
  "166": 3,
  "167": 5.05,
  "168": 3.19,
  "169": 7.915,
  "170": 3.48,
  "171": 4.86,
  "172": 5.8,
  "173": 3.59,
  "174": 7.915,
  "175": 3.19,
  "176": 4.015,
  "177": 5.8,
  "178": 3.33,
  "179": 3.33,
  "180": 3.19,
  "181": 5.6850000000000005,
  "182": 7.005,
  "183": 2.795,
  "184": 3.19,
  "185": 3.33,
  "186": 3.88,
  "187": 4.86,
  "188": 7.12,
  "189": 7.12,
  "190": 7.125,
  "191": 4.205,
  "192": 7.1000000000000005,
  "193": 7.1000000000000005,
  "194": 7.1000000000000005,
  "195": 7.1000000000000005,
  "196": 7.1000000000000005,
  "197": 7.1000000000000005,
  "198": 9.34,
  "199": 6.705,
  "200": 5.7250000000000005,
  "201": 5.7250000000000005,
  "202": 5.7250000000000005,
  "203": 5.7250000000000005,
  "204": 3.14,
  "205": 3.14,
  "206": 3.14,
  "207": 3.14,
  "208": 7.905,
  "209": 7.5600000000000005,
  "210": 7.995,
  "211": 7.995,
  "212": 7.995,
  "213": 7.995,
  "214": 7.995,
  "215": 5.8,
  "216": 7.995,
  "217": 7.28,
  "218": 7.28,
  "219": 7.28,
  "220": 7.28,
  "221": 6.545,
  "222": 6.25,
  "223": 6.3500000000000005,
  "224": 5.235,
  "225": 5.235,
  "226": 5.235,
  "227": 5.235,
  "228": 5.235,
  "229": 5.235,
  "230": 8.255,
  "231": 4.7700000000000005,
  "232": 5.345,
  "233": 5.345,
  "234": 5.345,
  "235": 5.345,
  "236": 2.71,
  "237": 2.71,
  "238": 2.71,
  "239": 2.71,
  "240": 5.68,
  "241": 5.6850000000000005,
  "242": 5.6850000000000005,
  "243": 5.6850000000000005,
  "244": 5.6850000000000005,
  "245": 5.6850000000000005,
  "246": 5.6850000000000005,
  "247": 5.8,
  "248": 5.6850000000000005,
  "249": 5.6850000000000005,
  "250": 5.6850000000000005,
  "251": 5.6850000000000005,
  "252": 5.6850000000000005,
  "253": 5.335,
  "254": 5.655,
  "255": 5.335,
  "260": 7.1000000000000005,
  "261": 5.235,
  "262": 6.705,
  "263": 4.7700000000000005,
  "280": 5.79,
  "281": 5.345,
  "305": 2.71,
  "321": 5.505,
  "322": 3.39,
  "323": 7.5600000000000005,
  "324": 5.6850000000000005,
  "338": 10.815,
  "339": 8.745000000000001,
  "346": 5.535,
  "347": 4.37,
  "352": 5.365,
  "353": 4.37,
  "376": 6.545,
  "377": 6.17,
  "378": 4.695,
  "379": 6.17,
  "380": 4.695,
  "381": 6.17,
  "382": 4.695,
  "402": 5.8,
  "710": 3.19,
  "711": 3.19,
  "713": 3.19,
  "728": 3.19,
  "729": 3.19,
  "730": 3.19,
  "731": 3.19,
  "732": 3.19,
  "733": 3.19,
  "960": 6.44,
  "8211": 5.6850000000000005,
  "8212": 8.42,
  "8216": 2.25,
  "8217": 2.25,
  "8218": 2.25,
  "8220": 3.91,
  "8221": 3.91,
  "8222": 3.91,
  "8224": 5.8,
  "8225": 5.8,
  "8226": 5.8,
  "8230": 7.585,
  "8240": 11.735,
  "8249": 3.21,
  "8250": 3.21,
  "8260": 3.555,
  "8364": 5.8,
  "8482": 7.26,
  "8486": 7.4,
  "8706": 5.8,
  "8710": 7.335,
  "8719": 6.86,
  "8721": 6.86,
  "8722": 5.8,
  "8730": 5.825,
  "8734": 6.86,
  "8747": 4.135,
  "8776": 5.8,
  "8800": 5.8,
  "8804": 5.8,
  "8805": 5.8,
  "9674": 5.8,
  "9833": 0,
  "64257": 6.01,
  "64258": 6.17,
  avg: 5.36520992366412
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/hooks/fontMetrics/Lato10Regular.ts
var Lato10RegularMetrics = {
  "13": 0,
  "32": 1.93,
  "33": 3.43,
  "34": 3.97,
  "35": 5.8,
  "36": 5.8,
  "37": 7.86,
  "38": 7.03,
  "39": 2.3000000000000003,
  "40": 3,
  "41": 3,
  "42": 4,
  "43": 5.8,
  "44": 2.12,
  "45": 3.47,
  "46": 2.12,
  "47": 3.73,
  "48": 5.8,
  "49": 5.8,
  "50": 5.8,
  "51": 5.8,
  "52": 5.8,
  "53": 5.8,
  "54": 5.8,
  "55": 5.8,
  "56": 5.8,
  "57": 5.8,
  "58": 2.52,
  "59": 2.52,
  "60": 5.8,
  "61": 5.8,
  "62": 5.8,
  "63": 3.98,
  "64": 8.22,
  "65": 6.8,
  "66": 6.47,
  "67": 6.8500000000000005,
  "68": 7.53,
  "69": 5.8100000000000005,
  "70": 5.66,
  "71": 7.34,
  "72": 7.5600000000000005,
  "73": 3.0700000000000003,
  "74": 4.44,
  "75": 6.8100000000000005,
  "76": 5.14,
  "77": 9.200000000000001,
  "78": 7.5600000000000005,
  "79": 7.98,
  "80": 6.11,
  "81": 7.98,
  "82": 6.44,
  "83": 5.3,
  "84": 5.9,
  "85": 7.3,
  "86": 6.8,
  "87": 10.19,
  "88": 6.43,
  "89": 6.29,
  "90": 6.24,
  "91": 3,
  "92": 3.75,
  "93": 3,
  "94": 5.8,
  "95": 3.94,
  "96": 3.0700000000000003,
  "97": 5.07,
  "98": 5.59,
  "99": 4.67,
  "100": 5.59,
  "101": 5.24,
  "102": 3.37,
  "103": 5.11,
  "104": 5.5600000000000005,
  "105": 2.56,
  "106": 2.54,
  "107": 5.24,
  "108": 2.56,
  "109": 8.21,
  "110": 5.5600000000000005,
  "111": 5.5600000000000005,
  "112": 5.5200000000000005,
  "113": 5.59,
  "114": 4.03,
  "115": 4.34,
  "116": 3.73,
  "117": 5.5600000000000005,
  "118": 5.12,
  "119": 7.66,
  "120": 5.04,
  "121": 5.12,
  "122": 4.62,
  "123": 3,
  "124": 3,
  "125": 3,
  "126": 5.8,
  "160": 1.93,
  "161": 3.43,
  "162": 5.8,
  "163": 5.8,
  "164": 5.8,
  "165": 5.8,
  "166": 3,
  "167": 5.03,
  "168": 3.0700000000000003,
  "169": 7.98,
  "170": 3.42,
  "171": 4.63,
  "172": 5.8,
  "173": 3.47,
  "174": 7.98,
  "175": 3.0700000000000003,
  "176": 3.97,
  "177": 5.8,
  "178": 3.3200000000000003,
  "179": 3.3200000000000003,
  "180": 3.0700000000000003,
  "181": 5.5600000000000005,
  "182": 6.69,
  "183": 2.73,
  "184": 3.0700000000000003,
  "185": 3.3200000000000003,
  "186": 3.81,
  "187": 4.63,
  "188": 7.12,
  "189": 7.12,
  "190": 7.13,
  "191": 3.98,
  "192": 6.8,
  "193": 6.8,
  "194": 6.8,
  "195": 6.8,
  "196": 6.8,
  "197": 6.8,
  "198": 9.290000000000001,
  "199": 6.8500000000000005,
  "200": 5.8100000000000005,
  "201": 5.8100000000000005,
  "202": 5.8100000000000005,
  "203": 5.8100000000000005,
  "204": 3.0700000000000003,
  "205": 3.0700000000000003,
  "206": 3.0700000000000003,
  "207": 3.0700000000000003,
  "208": 7.890000000000001,
  "209": 7.5600000000000005,
  "210": 7.98,
  "211": 7.98,
  "212": 7.98,
  "213": 7.98,
  "214": 7.98,
  "215": 5.8,
  "216": 7.98,
  "217": 7.3,
  "218": 7.3,
  "219": 7.3,
  "220": 7.3,
  "221": 6.29,
  "222": 6.11,
  "223": 6.09,
  "224": 5.07,
  "225": 5.07,
  "226": 5.07,
  "227": 5.07,
  "228": 5.07,
  "229": 5.07,
  "230": 8.16,
  "231": 4.67,
  "232": 5.24,
  "233": 5.24,
  "234": 5.24,
  "235": 5.24,
  "236": 2.56,
  "237": 2.56,
  "238": 2.56,
  "239": 2.56,
  "240": 5.53,
  "241": 5.5600000000000005,
  "242": 5.5600000000000005,
  "243": 5.5600000000000005,
  "244": 5.5600000000000005,
  "245": 5.5600000000000005,
  "246": 5.5600000000000005,
  "247": 5.8,
  "248": 5.5600000000000005,
  "249": 5.5600000000000005,
  "250": 5.5600000000000005,
  "251": 5.5600000000000005,
  "252": 5.5600000000000005,
  "253": 5.12,
  "254": 5.5200000000000005,
  "255": 5.12,
  "260": 6.8,
  "261": 5.07,
  "262": 6.8500000000000005,
  "263": 4.67,
  "280": 5.94,
  "281": 5.24,
  "305": 2.56,
  "321": 5.43,
  "322": 3.21,
  "323": 7.5600000000000005,
  "324": 5.5600000000000005,
  "338": 10.96,
  "339": 8.67,
  "346": 5.48,
  "347": 4.34,
  "352": 5.3,
  "353": 4.34,
  "376": 6.29,
  "377": 6.24,
  "378": 4.62,
  "379": 6.24,
  "380": 4.62,
  "381": 6.24,
  "382": 4.62,
  "402": 5.8,
  "710": 3.0700000000000003,
  "711": 3.0700000000000003,
  "713": 3.0700000000000003,
  "728": 3.0700000000000003,
  "729": 3.0700000000000003,
  "730": 3.0700000000000003,
  "731": 3.0700000000000003,
  "732": 3.0700000000000003,
  "733": 3.0700000000000003,
  "960": 6.18,
  "8211": 5.5600000000000005,
  "8212": 8.21,
  "8216": 2.12,
  "8217": 2.12,
  "8218": 2.12,
  "8220": 3.64,
  "8221": 3.64,
  "8222": 3.64,
  "8224": 5.8,
  "8225": 5.8,
  "8226": 5.8,
  "8230": 7.2700000000000005,
  "8240": 11.53,
  "8249": 3.16,
  "8250": 3.16,
  "8260": 3.0700000000000003,
  "8364": 5.8,
  "8482": 7.2,
  "8486": 7.47,
  "8706": 5.8,
  "8710": 6.96,
  "8719": 6.84,
  "8721": 6.84,
  "8722": 5.8,
  "8730": 5.73,
  "8734": 6.84,
  "8747": 4,
  "8776": 5.8,
  "8800": 5.8,
  "8804": 5.8,
  "8805": 5.8,
  "9674": 5.8,
  "9833": 0,
  "64257": 5.7,
  "64258": 5.94,
  avg: 5.27851145038167
};

// src/renderers/common/usePhaseBlock/usePhaseBlock.constants.ts
var DEFAULT_CELL_FONT_WEIGHT = "regular";

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/hooks/useBestColumnWidths.ts
var estimateCharacterWidth = (character, metrics) => {
  const charCode = character.charCodeAt(0).toString();
  if (typeof metrics[charCode] === "undefined") {
    return metrics["avg"];
  }
  return metrics[charCode];
};
var measureText = (text, metrics) => text.split("").map((char) => estimateCharacterWidth(char, metrics)).reduce((acc, curr) => acc + curr, 0);
var getStringsForColumnWeight = (pricingTables, column, fontWeight) => {
  const strings2 = pricingTables.flatMap((table) => table.rows).filter((row) => row[column]).flatMap((row) => {
    const cell = row[column];
    if (!cell || !("text" in cell)) {
      return void 0;
    }
    const cellFontWeight = cell.style?.fontWeight ?? DEFAULT_CELL_FONT_WEIGHT;
    if (cellFontWeight !== fontWeight) {
      return void 0;
    }
    return cell.text;
  }).filter(Boolean);
  return strings2;
};
var getStringsForColumn = (pricingTables, column) => ({
  regular: getStringsForColumnWeight(pricingTables, column, "regular"),
  bold: getStringsForColumnWeight(pricingTables, column, "bold")
});
var calculateColumnWidth = (pricingTables, column) => {
  const columnStrings = getStringsForColumn(pricingTables, column);
  const regularWidths = columnStrings.regular.map(
    (text) => measureText(text, Lato10RegularMetrics)
  );
  const boldWidths = columnStrings.bold.map(
    (text) => measureText(text, Lato10BoldMetrics)
  );
  const allWidths = [...regularWidths, ...boldWidths].map(
    (width) => width + DEFAULT_COLUMN_PADDING[column].left + DEFAULT_COLUMN_PADDING[column].right
  ).sort((a, b) => b - a);
  if (allWidths.length === 0) {
    return DEFAULT_COLUMN_WIDTHS[column];
  }
  const biggestWidth = Math.ceil(allWidths[0]);
  return biggestWidth < DEFAULT_COLUMN_WIDTHS[column] ? DEFAULT_COLUMN_WIDTHS[column] : biggestWidth;
};
var useBestColumnWidths2 = ({ pricingTables }) => {
  return {
    quantity: calculateColumnWidth(pricingTables, "quantity"),
    rate: (() => {
      const bestColumnWidthDiscountedRate = calculateColumnWidth(
        pricingTables,
        "discountedRate"
      );
      if (bestColumnWidthDiscountedRate > 0) {
        return calculateColumnWidth(pricingTables, "rate") + calculateColumnWidth(pricingTables, "discountedRate") + RATE_DISCOUNTED_RATE_GAP_PX;
      }
      return calculateColumnWidth(pricingTables, "rate");
    })(),
    discount: calculateColumnWidth(pricingTables, "discount")
  };
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/PhaseBlockMeta.tsx
import { View as View15, Text as Text29 } from "@react-pdf/renderer";
import { toMoney as toMoney4 } from "@sequencehq/core-models";
import { jsx as jsx46, jsxs as jsxs27 } from "react/jsx-runtime";
var PhaseBlockMeta2 = ({
  meta,
  currency
}) => {
  const { lato10BoldGreyGrey100: lato10BoldGreyGrey1003, lato10RegularGreyGrey60: lato10RegularGreyGrey604, lineThrough } = PdfQuoteRenderer_styles_default;
  if (!meta) {
    return null;
  }
  const shouldShowTotal = Boolean(meta.totalFullRate && meta.totalFullRate > 0);
  if (!shouldShowTotal) {
    return null;
  }
  const shouldShowDiscountedRate = typeof meta?.totalDiscountedRate !== "undefined" && typeof meta?.totalFullRate !== "undefined" && meta.totalFullRate !== meta.totalDiscountedRate;
  return /* @__PURE__ */ jsx46(View15, { style: { textAlign: "right" }, children: /* @__PURE__ */ jsxs27(Text29, { style: lato10BoldGreyGrey1003, children: [
    "Total:",
    " ",
    /* @__PURE__ */ jsx46(
      Text29,
      {
        style: shouldShowDiscountedRate ? [lato10RegularGreyGrey604, lineThrough] : [lato10BoldGreyGrey1003],
        children: toMoney4({ currency, value: `${meta.totalFullRate}` })
      }
    ),
    " ",
    shouldShowDiscountedRate && /* @__PURE__ */ jsx46(Text29, { style: lato10BoldGreyGrey1003, children: toMoney4({ currency, value: `${meta.totalDiscountedRate}` }) })
  ] }) });
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/Table/PhaseBlockTables.tsx
import { Fragment as Fragment10, jsx as jsx47, jsxs as jsxs28 } from "react/jsx-runtime";
var PhaseBlockTables2 = ({
  pricingTables,
  defaultVisibleColumns,
  currency,
  meta
}) => {
  const bestColumnWidths = useBestColumnWidths2({ pricingTables });
  return /* @__PURE__ */ jsxs28(Fragment10, { children: [
    /* @__PURE__ */ jsx47(PhaseBlockTableContainer2, { children: pricingTables.map((pricingTable) => /* @__PURE__ */ jsx47(
      PhaseBlockTable2,
      {
        pricingTable,
        bestColumnWidths,
        defaultVisibleColumns
      },
      pricingTable.uuid
    )) }),
    /* @__PURE__ */ jsx47(PhaseBlockMeta2, { meta, currency })
  ] });
};

// src/renderers/PdfQuoteRenderer/blocks/PhaseBlock/PhaseBlock.tsx
import { jsx as jsx48 } from "react/jsx-runtime";
var PhaseBlock2 = ({ config, settings, meta }) => {
  const { pricingTables, defaultVisibleColumns } = usePhaseBlock({
    config,
    settings,
    context: "pdf"
  });
  const currency = config.phases[0].prices[0].currency;
  return /* @__PURE__ */ jsx48(
    PhaseBlockTables2,
    {
      pricingTables,
      defaultVisibleColumns,
      currency,
      meta
    }
  );
};

// src/renderers/PdfQuoteRenderer/blocks/BulletListBlock/BulletListBlockV2.tsx
import { View as View16, Text as Text30 } from "@react-pdf/renderer";
import { GreyGrey80 as GreyGrey8013 } from "@sequencehq/design-tokens";
import { jsx as jsx49, jsxs as jsxs29 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey806 } = PdfQuoteRenderer_styles_default;
var BulletListBlockV22 = ({ config: { items }, font }) => /* @__PURE__ */ jsx49(View16, { style: { display: "flex", gap: "3px" }, children: items.map((item, index) => {
  const renderedText = renderText2({
    text: item,
    blockTextColor: GreyGrey8013,
    font,
    blockBackgroundColor: "transparent"
  });
  if (!renderedText) {
    return null;
  }
  return /* @__PURE__ */ jsxs29(
    View16,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "8px"
      },
      children: [
        /* @__PURE__ */ jsx49(Text30, { style: lato10RegularGreyGrey806, children: "\u2022" }),
        /* @__PURE__ */ jsx49(
          View16,
          {
            style: {
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              flex: 1
            },
            children: renderedText
          }
        )
      ]
    },
    index
  );
}) });

// src/renderers/PdfQuoteRenderer/blocks/NumberListBlock/NumberListBlockV2.tsx
import { View as View17, Text as Text31 } from "@react-pdf/renderer";
import { GreyGrey80 as GreyGrey8014 } from "@sequencehq/design-tokens";
import { jsx as jsx50, jsxs as jsxs30 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey807 } = PdfQuoteRenderer_styles_default;
var estimateOrdinalWidth2 = (numberOfItems) => {
  const maxOrdinal = `${String(numberOfItems + 1)}`;
  return `${maxOrdinal.length * 10}px`;
};
var NumberListBlockV22 = ({ config: { items }, font }) => /* @__PURE__ */ jsx50(View17, { style: { display: "flex", gap: "4.5px" }, children: items.map((item, index) => {
  const ordinal = `${String(index + 1)}.`;
  const renderedText = renderText2({
    text: item,
    blockTextColor: GreyGrey8014,
    font,
    blockBackgroundColor: "transparent"
  });
  if (!renderedText) {
    return null;
  }
  return /* @__PURE__ */ jsxs30(
    View17,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "8px"
      },
      children: [
        /* @__PURE__ */ jsx50(
          Text31,
          {
            style: {
              ...lato10RegularGreyGrey807,
              width: estimateOrdinalWidth2(items.length)
            },
            children: ordinal
          }
        ),
        /* @__PURE__ */ jsx50(
          View17,
          {
            style: {
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              flex: 1
            },
            children: renderedText
          }
        )
      ]
    },
    index
  );
}) });

// src/renderers/PdfQuoteRenderer/blocks/BulletListBlock/BulletListBlockV3.tsx
import { View as View18, Text as Text32 } from "@react-pdf/renderer";
import { jsx as jsx51, jsxs as jsxs31 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey808 } = PdfQuoteRenderer_styles_default;
var BulletListBlockV32 = ({ config: { items }, font }) => /* @__PURE__ */ jsx51(View18, { style: { display: "flex", gap: "3px" }, children: items.map((item, index) => {
  const blockTextColor = mapTextColor(item.textColor);
  const renderedText = renderText2({
    text: item.text,
    blockTextColor,
    font,
    blockBackgroundColor: "transparent"
  });
  if (!renderedText) {
    return null;
  }
  return /* @__PURE__ */ jsxs31(
    View18,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "8px",
        backgroundColor: mapBackgroundColor(item.backgroundColor)
      },
      children: [
        /* @__PURE__ */ jsx51(Text32, { style: { ...lato10RegularGreyGrey808, color: blockTextColor }, children: "\u2022" }),
        /* @__PURE__ */ jsx51(
          View18,
          {
            style: {
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              flex: 1
            },
            children: renderedText
          }
        )
      ]
    },
    index
  );
}) });

// src/renderers/PdfQuoteRenderer/blocks/NumberListBlock/NumberListBlockV3.tsx
import { View as View19, Text as Text33 } from "@react-pdf/renderer";
import { jsx as jsx52, jsxs as jsxs32 } from "react/jsx-runtime";
var { lato10RegularGreyGrey80: lato10RegularGreyGrey809 } = PdfQuoteRenderer_styles_default;
var estimateOrdinalWidth3 = (numberOfItems) => {
  const maxOrdinal = `${String(numberOfItems + 1)}`;
  return `${maxOrdinal.length * 10}px`;
};
var NumberListBlockV32 = ({ config: { items }, font }) => /* @__PURE__ */ jsx52(View19, { style: { display: "flex", gap: "4.5px" }, children: items.map((item, index) => {
  const ordinal = `${String(index + 1)}.`;
  const blockTextColor = mapTextColor(item.textColor);
  const renderedText = renderText2({
    text: item.text,
    blockTextColor,
    font,
    blockBackgroundColor: "transparent"
  });
  if (!renderedText) {
    return null;
  }
  return /* @__PURE__ */ jsxs32(
    View19,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "8px",
        backgroundColor: mapBackgroundColor(item.backgroundColor)
      },
      children: [
        /* @__PURE__ */ jsx52(
          Text33,
          {
            style: {
              ...lato10RegularGreyGrey809,
              width: estimateOrdinalWidth3(items.length),
              color: blockTextColor
            },
            children: ordinal
          }
        ),
        /* @__PURE__ */ jsx52(
          View19,
          {
            style: {
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              flex: 1
            },
            children: renderedText
          }
        )
      ]
    },
    index
  );
}) });

// src/renderers/PdfQuoteRenderer/blocks/Blocks.tsx
import { jsx as jsx53 } from "react/jsx-runtime";
var {
  lato13BoldGreyGrey80,
  lato12BoldGreyGrey80,
  lato10RegularGreyGrey80: lato10RegularGreyGrey8010,
  lato10BoldGreyGrey80: lato10BoldGreyGrey803
} = PdfQuoteRenderer_styles_default;
var Blocks = ({ hook }) => {
  const blocks = useMemo10(
    () => hook.blocks.map((block) => ({
      ...block,
      uuid: uuidv46()
    })),
    [hook.blocks]
  );
  const renderedBlocks = blocks.map(
    (block) => match13(block).with({ blockType: "heading1" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx53(TextBlock2, { config, font: lato13BoldGreyGrey80 }, uuid);
    }).with({ blockType: "heading2" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx53(TextBlock2, { config, font: lato12BoldGreyGrey80 }, uuid);
    }).with({ blockType: "heading3" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx53(TextBlock2, { config, font: lato10BoldGreyGrey803 }, uuid);
    }).with({ blockType: "paragraph" }, ({ uuid, config }) => {
      if (!isTextBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx53(
        TextBlock2,
        {
          config,
          font: lato10RegularGreyGrey8010
        },
        uuid
      );
    }).with({ blockType: "bulletList" }, ({ uuid, config, version }) => {
      if (version === 3 && isListBlockConfigV3(config)) {
        return /* @__PURE__ */ jsx53(
          BulletListBlockV32,
          {
            config,
            font: lato10RegularGreyGrey8010
          },
          uuid
        );
      }
      if (version === 2 && isListBlockConfigV2(config)) {
        return /* @__PURE__ */ jsx53(
          BulletListBlockV22,
          {
            config,
            font: lato10RegularGreyGrey8010
          },
          uuid
        );
      }
      if (version === 1 && isListBlockConfig(config)) {
        return /* @__PURE__ */ jsx53(BulletListBlock2, { config }, uuid);
      }
      return null;
    }).with({ blockType: "numberList" }, ({ uuid, config, version }) => {
      if (version === 3 && isListBlockConfigV3(config)) {
        return /* @__PURE__ */ jsx53(
          NumberListBlockV32,
          {
            config,
            font: lato10RegularGreyGrey8010
          },
          uuid
        );
      }
      if (version === 2 && isListBlockConfigV2(config)) {
        return /* @__PURE__ */ jsx53(
          NumberListBlockV22,
          {
            config,
            font: lato10RegularGreyGrey8010
          },
          uuid
        );
      }
      if (version === 1 && isListBlockConfig(config)) {
        return /* @__PURE__ */ jsx53(NumberListBlock2, { config }, uuid);
      }
      return null;
    }).with({ blockType: "image" }, ({ uuid, config }) => {
      if (!isImageBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx53(
        ImageBlock2,
        {
          config,
          isRenderedInBrowser: hook.isRenderedInBrowser,
          imageData: hook.imageData
        },
        uuid
      );
    }).with({ blockType: "phases" }, ({ uuid, config }) => {
      if (!isPhaseBlockConfig(config)) {
        return null;
      }
      return /* @__PURE__ */ jsx53(
        PhaseBlock2,
        {
          config,
          settings: hook.settings,
          meta: hook.meta
        },
        uuid
      );
    }).with({ blockType: "coverImage" }, () => null).exhaustive()
  );
  return /* @__PURE__ */ jsx53(View20, { style: { display: "flex", gap: "6px" }, wrap: true, children: renderedBlocks });
};

// src/renderers/PdfQuoteRenderer/pages/PageQuoteContent.tsx
import { jsx as jsx54, jsxs as jsxs33 } from "react/jsx-runtime";
var PageQuoteContent = ({ hook }) => /* @__PURE__ */ jsxs33(PageLayout, { hook, children: [
  /* @__PURE__ */ jsx54(
    View21,
    {
      style: {
        display: "flex",
        gap: "16px",
        marginBottom: "24px"
      },
      children: /* @__PURE__ */ jsx54(Header2, { hook })
    }
  ),
  /* @__PURE__ */ jsx54(Blocks, { hook })
] });

// src/renderers/PdfQuoteRenderer/pages/PageSignature.tsx
import { Text as Text34, View as View22 } from "@react-pdf/renderer";
import { GreyGrey30 as GreyGrey309, GreyGrey60 as GreyGrey609 } from "@sequencehq/design-tokens";
import { jsx as jsx55, jsxs as jsxs34 } from "react/jsx-runtime";
var {
  lato13BoldGreyGrey80: lato13BoldGreyGrey802,
  lato10BoldGreyGrey80: lato10BoldGreyGrey804,
  lato10RegularGreyGrey80: lato10RegularGreyGrey8011,
  lato10RegularGreyGrey60: lato10RegularGreyGrey603,
  lato10RegularHidden
} = PdfQuoteRenderer_styles_default;
var PageSignature = ({ hook }) => {
  return /* @__PURE__ */ jsxs34(PageLayout, { hook, children: [
    /* @__PURE__ */ jsxs34(
      View22,
      {
        style: {
          display: "flex",
          flexDirection: "row",
          gap: "16px",
          paddingBottom: "24px",
          borderBottomWidth: "1px",
          borderBottomColor: GreyGrey309,
          borderBottomStyle: "solid"
        },
        children: [
          /* @__PURE__ */ jsx55(Text34, { style: { ...lato13BoldGreyGrey802, flex: "1" }, children: "Signature" }),
          /* @__PURE__ */ jsx55(
            Text34,
            {
              style: { ...lato10RegularGreyGrey8011, width: "280px" },
              hyphenationCallback: (words) => [words],
              children: "Please find your name below and sign this document to execute this order."
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx55(
      View22,
      {
        style: {
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginTop: "32px"
        },
        children: hook.signers.map((signer, index) => /* @__PURE__ */ jsx55(
          Signer,
          {
            email: signer.email,
            name: signer.name,
            index: index + 1
          },
          signer.email
        ))
      }
    )
  ] });
};
var Signer = ({ name, email, index }) => {
  return /* @__PURE__ */ jsxs34(
    View22,
    {
      style: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "16px"
      },
      wrap: false,
      children: [
        /* @__PURE__ */ jsxs34(View22, { style: { display: "flex", flexDirection: "column", flex: "1" }, children: [
          /* @__PURE__ */ jsx55(Text34, { style: lato10BoldGreyGrey804, children: name }),
          /* @__PURE__ */ jsx55(Text34, { style: lato10RegularGreyGrey603, children: email })
        ] }),
        /* @__PURE__ */ jsx55(
          View22,
          {
            style: {
              width: "280px",
              height: "48px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px",
              borderRadius: "2px",
              borderColor: GreyGrey609
            },
            children: /* @__PURE__ */ jsx55(Text34, { style: lato10RegularHidden, children: `[sig|req|signer${index}]` })
          }
        )
      ]
    }
  );
};

// src/renderers/PdfQuoteRenderer/hooks/usePdfQuoteRenderer.tsx
import { useMemo as useMemo11 } from "react";
import { match as match14 } from "ts-pattern";
import { format as format3 } from "@sequencehq/utils/dates";
var usePdfQuoteRenderer = ({
  data,
  isRenderedInBrowser,
  imageData
}) => {
  const startDate = useMemo11(() => {
    if (!data.preliminaryBillingStartDate) {
      return void 0;
    }
    return format3(new Date(data.preliminaryBillingStartDate), "d LLLL yyyy");
  }, [data.preliminaryBillingStartDate]);
  const issueDate = useMemo11(() => {
    if (!data.publishedAt) {
      return void 0;
    }
    return format3(new Date(data.publishedAt), "d LLLL yyyy");
  }, [data.publishedAt]);
  const validUntilDate = useMemo11(() => {
    if (!data.expiresAt) {
      return void 0;
    }
    return format3(new Date(data.expiresAt), "d LLLL yyyy");
  }, [data.expiresAt]);
  const title = useMemo11(() => data.title || "Untitled", [data.title]);
  const footerText = useMemo11(() => {
    if (!data.prospectName) {
      return title;
    }
    return `${title} for ${data.prospectName}`;
  }, [data.prospectName, title]);
  const dealType = useMemo11(() => {
    return match14(data.dealType).with("NEW_BUSINESS", () => "New Business").with("RENEWAL", () => "Renewal").exhaustive();
  }, [data.dealType]);
  const presentation = apiPresentationToQuotePresentation(data.presentation);
  return {
    title,
    contractLength: renderContractLength(data.phases),
    startDate,
    issueDate,
    validUntilDate,
    prospectName: data.prospectName,
    prospectAddress: data.prospectAddress,
    merchantName: data.merchantName,
    merchantAddress: data.merchantAddress,
    merchantEmailAddress: data.merchantEmailAddress,
    merchantLogoUrl: data.merchantLogoUrl,
    brandColour: data.merchantBrandColour,
    footerText,
    blocks: presentation.blocks,
    isRenderedInBrowser,
    imageData,
    signers: data.signers,
    dealType,
    includeSignaturePage: data.includeSignaturePage,
    meta: {
      totalFullRate: data.totalFullRate,
      totalDiscountedRate: data.totalDiscountedRate
    },
    settings: presentation.settings
  };
};

// src/renderers/PdfQuoteRenderer/PdfQuoteRenderer.tsx
import { registerReactPdfFonts } from "@sequencehq/utils";
import { jsx as jsx56, jsxs as jsxs35 } from "react/jsx-runtime";
registerReactPdfFonts();
var PdfQuoteRenderer = ({
  isRenderedInBrowser,
  data,
  imageData
}) => {
  const pdfQuoteRendererHook = usePdfQuoteRenderer({
    data,
    isRenderedInBrowser,
    imageData
  });
  return /* @__PURE__ */ jsxs35(Document, { children: [
    /* @__PURE__ */ jsx56(PageQuoteContent, { hook: pdfQuoteRendererHook }),
    data.includeSignaturePage && /* @__PURE__ */ jsx56(PageSignature, { hook: pdfQuoteRendererHook })
  ] });
};

// src/renderers/PdfQuoteRenderer/hooks/useQuotePdfDownload.tsx
import { useCallback as useCallback6 } from "react";
import { pdf } from "@react-pdf/renderer";
import * as Sentry from "@sentry/react";

// src/renderers/PdfQuoteRenderer/helpers/getPdfImageData.ts
import { getSignedAssetUrl } from "@sequencehq/utils";
var getPdfImageData = async ({
  presentation,
  assetLoader,
  getImageDimensions
}) => {
  const { blocks } = apiPresentationToQuotePresentation(presentation);
  const imageConfigs = blocks.filter(
    (block) => block.blockType === "image" && isImageBlockConfig(block.config)
  ).map((block) => block.config);
  const imageData = (await Promise.all(
    imageConfigs.map(async (imageConfig) => {
      const signedUrlRes = await getSignedAssetUrl({
        url: imageConfig.src,
        assetLoader
      });
      if (!signedUrlRes) {
        return void 0;
      }
      const dimensions = await getImageDimensions(signedUrlRes);
      return {
        urlUnsigned: imageConfig.src,
        urlSigned: signedUrlRes,
        ...dimensions
      };
    })
  )).filter(Boolean);
  return imageData;
};

// src/renderers/PdfQuoteRenderer/hooks/useQuotePdfDownload.tsx
import {
  downloadBlob,
  isExternalAssetUrl,
  registerReactPdfFonts as registerReactPdfFonts2
} from "@sequencehq/utils";
import { jsx as jsx57 } from "react/jsx-runtime";
registerReactPdfFonts2();
var useQuotePdfDownload = ({
  notificationHook,
  fetchPdfQuoteRendererData,
  assetLoader
}) => {
  const notifications = notificationHook();
  const showToastPreparing = useCallback6(() => {
    notifications.displayNotification("Preparing download", {
      id: "download-pdf",
      type: "neutral",
      isClosable: false
    });
  }, [notifications]);
  const showToastSuccess = useCallback6(() => {
    notifications.displayNotification("Download ready", {
      id: "download-pdf-success",
      type: "success",
      isClosable: false,
      duration: 3e3
    });
  }, [notifications]);
  const showToastError = useCallback6(() => {
    notifications.displayNotification("PDF download failed", {
      id: "download-pdf-error",
      type: "error",
      isClosable: false,
      duration: 3e3
    });
  }, [notifications]);
  const onClick = useCallback6(async () => {
    try {
      showToastPreparing();
      const pdfQuoteRendererData = await fetchPdfQuoteRendererData();
      if (!pdfQuoteRendererData) {
        throw new Error();
      }
      const imageData = await getPdfImageData({
        presentation: pdfQuoteRendererData.presentation,
        assetLoader,
        getImageDimensions: async (url) => {
          const imageRes = await fetch(url, {
            headers: isExternalAssetUrl(url) ? {} : { "sequence-skip-optimizer": "true" }
          });
          if (!imageRes.ok) {
            return { height: void 0, width: void 0 };
          }
          const imageBlob = await imageRes.blob();
          const imageBlobObjectUrl = URL.createObjectURL(imageBlob);
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              URL.revokeObjectURL(imageBlobObjectUrl);
              return resolve({ height: img.height, width: img.width });
            };
            img.onerror = () => {
              URL.revokeObjectURL(imageBlobObjectUrl);
              return resolve({ height: void 0, width: void 0 });
            };
            img.src = imageBlobObjectUrl;
          });
        }
      });
      const blob = await pdf(
        /* @__PURE__ */ jsx57(
          PdfQuoteRenderer,
          {
            data: pdfQuoteRendererData,
            isRenderedInBrowser: true,
            imageData
          }
        )
      ).toBlob();
      downloadBlob(`${pdfQuoteRendererData.quoteNumber}.pdf`)(blob);
      showToastSuccess();
    } catch (e) {
      showToastError();
      Sentry.captureException(e);
    }
  }, [
    fetchPdfQuoteRendererData,
    showToastError,
    showToastPreparing,
    showToastSuccess,
    assetLoader
  ]);
  return {
    onClick
  };
};

// src/renderers/PdfQuoteRenderer/hooks/useRemoteQuotePdfDownload.tsx
import { useCallback as useCallback7 } from "react";
import * as Sentry2 from "@sentry/react";
import { downloadBlob as downloadBlob2 } from "@sequencehq/utils";
var useRemoteQuotePdfDownload = ({
  quoteNumber,
  downloadPdfEndpoint,
  notificationHook
}) => {
  const notifications = notificationHook();
  const showToastPreparing = useCallback7(() => {
    notifications.displayNotification("Preparing download", {
      id: "download-pdf",
      type: "neutral",
      isClosable: false
    });
  }, [notifications]);
  const showToastSuccess = useCallback7(() => {
    notifications.displayNotification("Download ready", {
      id: "download-pdf-success",
      type: "success",
      isClosable: false,
      duration: 3e3
    });
  }, [notifications]);
  const showToastError = useCallback7(() => {
    notifications.displayNotification("PDF download failed", {
      id: "download-pdf-error",
      type: "error",
      isClosable: false,
      duration: 3e3
    });
  }, [notifications]);
  const onClick = useCallback7(async () => {
    try {
      showToastPreparing();
      const response = await downloadPdfEndpoint();
      if (!response.data || response.error) {
        throw new Error("Unable to download remote quote PDF");
      }
      downloadBlob2(`${quoteNumber}.pdf`)(response.data);
      showToastSuccess();
    } catch (e) {
      showToastError();
      Sentry2.captureException(e);
    }
  }, [
    downloadPdfEndpoint,
    quoteNumber,
    showToastError,
    showToastPreparing,
    showToastSuccess
  ]);
  return {
    onClick
  };
};
export {
  ContactRow,
  PdfQuoteRenderer,
  PhaseBlockTables2 as PhaseBlockTables,
  PopoverContacts,
  PopoverCustomerCard,
  PhaseBlockTables as ReactPhaseBlockTables,
  ReactQuoteRenderer,
  apiPresentationToQuotePresentation,
  getPdfImageData,
  isCoverImageBlockConfig,
  isCoverImageBlockConfigV2,
  isImageBlockConfig,
  isListBlockConfig,
  isListBlockConfigV2,
  isListBlockConfigV3,
  isPhaseBlockConfig,
  isQuoteBlock,
  isTextBlockConfig,
  presentationBlocksToQuoteBlocks,
  renderContractLength,
  useQuotePdfDownload,
  useRemoteQuotePdfDownload
};
//# sourceMappingURL=index.js.map